import React from 'react';
import { USERS, PROPERTIES, SCENARIOS } from '../../firebase/index';
import { getNumberFromString } from '../utility/formatNumbers';
import getSum from '../utility/getSum';
import { getTableSum } from '../utility/getTableSum';
import convertUnitMoney from '../utility/convertUnitMoney';
import { yearlyPercentFromYearlyValue } from '../utility/percentageConverters';
import FormUIOptions from '../components/formBuilder/formElements/formUIOptions';
import { isValue } from '../utility/isValue';
import {
  mortgagePrincipalOutstanding,
  mortgageFormSectionCalculator,
} from '../utility/mortgageCalculator';
import CMHCInsurancePremiums from '../constants/cmhcPremiums';
import { FaArrowUp, FaArrowDown } from 'react-icons/fa6';
import { TiPlus } from 'react-icons/ti';
import getNumberWithCommas from '../../../functions/createReport/getNumberWithCommas';
import criteriaDefaults from '../constants/investmentCriteria';

export default {
  title: 'Scenario Parameters',
  description:
    'Use this form to enter the information about additional investment and the income and expense values that will change as the result of this scenario',
  messageOnFinish: 'Proceed to the next steps to generate a property report',
  pathToSource: [
    {
      type: 'collection',
      value: USERS,
    },
    {
      type: 'user',
      value: 'uid',
    },
    {
      type: 'collection',
      value: PROPERTIES,
    },
    {
      type: 'param',
      value: 'propertyId',
    },
    {
      type: 'collection',
      value: SCENARIOS,
    },
  ],

  inputs: {
    title: 'Refinancing Worksheet',
    description:
      'Fill out the form below to see how different factors affect your investment',
    submitButtons: 'refinancing',
    submitButtonLabel: 'Submit & Analyze',
    submitButtonDescription: 'Submit the form to generate the BRRRR analysis',
    sideBarSchema: true,
    schema: [
      {
        sectionLabel: 'New Mortgage',
        sectionDescription:
          'Enter the details of your new mortgage(s). Default values are populated from your original mortgage details.',
        sectionValue: 'mortgageDetails',
        schema: [
          {
            type: FormUIOptions.Calculated,
            label: 'New Value',
            layout: 'default',
            name: 'newMarketValue',
            tooltipHeading: 'New Value',
            description: `Based on net operating income and cap rate, or the manually entered ARV`,
            tooltipFormula: [
              'New Value',
              '=',
              'Net Annual Operating Income',
              '/',
              'Cap Rate',
            ],
            unit: '$',
            derivedFrom: ['newMarketValue'],
            calcFunction: (newMarketValue) => {
              return newMarketValue;
            },
          },
          {
            type: FormUIOptions.Number,
            label: 'Loan-to-Value',
            tooltipHeading: 'Loan-to-Value',
            tooltipFormula: [
              'Loan-to-Value',
              '=',
              'Mortgage Amount',
              '/',
              'New Value',
            ],
            description:
              'By default, this is pre-populated with your original loan-to-value ratio',
            layout: 'default',
            name: 'loanToValueRatio',
            required: true,
            unit: '%',
            derivedFrom: [
              'loanToValueRatio',
              'totalMortgageAmount',
              'purchasePrice',
            ],
            calcFunction: (
              loanToValueRatio,
              totalMortgageAmount,
              purchasePrice
            ) => {
              if (isValue(loanToValueRatio)) {
                return loanToValueRatio;
              } else {
                return Math.round((totalMortgageAmount / purchasePrice) * 100);
              }
            },
          },

          {
            type: FormUIOptions.Number,
            label: 'Interest Rate',
            layout: 'default',
            name: 'newInterestRate',
            unit: '%',
            required: true,
            derivedFrom: ['interestRate', 'newInterestRate'],
            calcFunction: (interestRate, newInterestRate) => {
              if (isValue(newInterestRate)) {
                return newInterestRate;
              } else {
                return interestRate;
              }
            },
          },
          {
            type: FormUIOptions.YesNo,
            label: 'Interest-only?',
            layout: 'default',
            name: 'newMortgageInterestOnly',
            defaultValue: false,
            required: true,
          },
          {
            type: FormUIOptions.Number,
            label: 'Loan Period',
            description: 'How long until the principal of your loan is due?',
            layout: 'default',
            name: 'newInterestOnlyLoanPeriod',
            unit: 'years',
            condition: {
              field: 'newMortgageInterestOnly',
              isLocal: true,
              value: true,
            },
          },
          {
            type: FormUIOptions.YesNo,
            label: 'Interest paid monthly?',
            layout: 'default',
            name: 'newInterestOnlyMonthly',
            defaultValue: false,
            required: true,
            description:
              'Is the interest on your loan paid monthly or at the end of the loan period?',
            condition: {
              field: 'newMortgageInterestOnly',
              isLocal: true,
              value: true,
            },
          },
          {
            type: FormUIOptions.Number,
            label: 'Amortization',
            layout: 'default',
            name: 'newAmortization',
            unit: 'years',
            description: 'Mortgage period',
            required: true,
            condition: {
              field: 'newMortgageInterestOnly',
              isLocal: true,
              value: false,
            },
            derivedFrom: ['amortization', 'newAmortization'],
            calcFunction: (amortization, newAmortization) => {
              if (isValue(newAmortization)) {
                return newAmortization;
              } else {
                return amortization;
              }
            },
          },
          {
            type: FormUIOptions.YesNo,
            label: 'Include CMHC Premium?',
            description:
              'Canada Mortgage and Housing Corporation insurance, for properties in Canada, assuming the property is classified as a standard rental. If you are using the CMHC MLI select plan or another property type, you will need to enter the insurance premium amount manually.',
            name: 'shouldCalculateCMHCNew',
            layout: 'default',
            defaultValue: false,
            condition: [
              {
                field: 'newMortgageInterestOnly',
                isLocal: true,
                value: false,
              },
              {
                field: 'propertyInCanada',
                isLocal: true,
                value: [undefined, null, 1, ''],
              },
            ],
          },
          {
            type: FormUIOptions.YesNo,
            label: 'Calculate for me?',
            description:
              'If you select no, you will need to enter the insurance premium amount manually',
            name: 'shouldCalculateCMHCForMeNew',
            layout: 'default',
            defaultValue: true,
            condition: [
              {
                field: 'shouldCalculateCMHCNew',
                isLocal: true,
                value: true,
              },

              {
                field: 'newMortgageInterestOnly',
                isLocal: true,
                value: false,
              },
              {
                field: 'propertyInCanada',
                isLocal: true,
                value: [undefined, null, 1, ''],
              },
            ],
          },
          {
            type: FormUIOptions.Money,
            label: 'CMHC/Insurance Premium',
            layout: 'default',
            name: 'cmhcPremiumManualNew',
            unit: '$',
            condition: [
              {
                field: 'shouldCalculateCMHCNew',
                isLocal: true,
                value: true,
              },
              {
                field: 'shouldCalculateCMHCForMeNew',
                isLocal: true,
                value: false,
              },

              {
                field: 'newMortgageInterestOnly',
                isLocal: true,
                value: false,
              },
              {
                field: 'propertyInCanada',
                isLocal: true,
                value: [undefined, null, 1, ''],
              },
            ],
          },
          {
            type: FormUIOptions.Calculated,
            label: 'CMHC Premium',
            layout: 'default',
            name: 'cmhcPremiumNew',
            unit: '$',
            derivedFrom: [
              'loanToValueRatio',
              'shouldCalculateCMHCNew',
              'shouldCalculateCMHCForMeNew',
              'cmhcPremiumManualNew',
              'newMortgageInterestOnly',
              'newMarketValue',
              'propertyInCanada',
            ],
            calcFunction: (
              loanToValueRatio,
              shouldCalculateCMHC,
              shouldCalculateCMHCForMe,
              cmhcPremiumManual,
              mortgageInterestOnly,
              newMarketValue,
              propertyInCanada
            ) => {
              if (!shouldCalculateCMHCForMe) {
                return cmhcPremiumManual;
              }
              if (
                !shouldCalculateCMHC ||
                mortgageInterestOnly ||
                propertyInCanada === 0
              ) {
                return null;
              }

              const premiums = CMHCInsurancePremiums.rental;
              const ltv = loanToValueRatio / 100;
              const mortgageAmount = ltv * getNumberFromString(newMarketValue);

              switch (true) {
                case ltv <= 0.65:
                  return mortgageAmount * premiums[0.65];
                case ltv <= 0.7:
                  return mortgageAmount * premiums[0.7];
                case ltv <= 0.75:
                  return mortgageAmount * premiums[0.75];
                case ltv <= 0.8:
                  return mortgageAmount * premiums[0.8];
                default:
                  return mortgageAmount * premiums[0.85];
              }
            },
            condition: [
              {
                field: 'shouldCalculateCMHCNew',
                isLocal: true,
                value: true,
              },
              {
                field: 'newMortgageInterestOnly',
                isLocal: true,
                value: false,
              },
              {
                field: 'propertyInCanada',
                isLocal: true,
                value: [undefined, null, 1, ''],
              },
            ],
          },
          {
            type: FormUIOptions.TableModal,
            layout: 'default',
            itemName: 'Additional Loan',
            groupStart: true,
            name: 'BRRRRAdditionalFinancing',
            modalDescription: 'Add information about your loan',
            tableSchema: [
              {
                type: FormUIOptions.Text,
                label: 'Loan Name',
                layout: 'default',
                name: 'loanNameTable',
                before: true,
              },
              {
                type: FormUIOptions.Money,
                label: 'Financing Amount',
                layout: 'default',
                name: 'mortgageAmountTable',
                required: true,
                unit: '$',
                before: true,
              },
              {
                type: FormUIOptions.Number,
                label: 'Interest Rate',
                layout: 'default',
                name: 'interestRateTable',
                unit: '%',
                required: true,
                before: true,
              },
              {
                type: FormUIOptions.YesNo,
                label: 'Interest-only?',
                layout: 'default',
                name: 'mortgageInterestOnlyTable',
                defaultValue: false,
                before: true,
              },
              {
                type: FormUIOptions.Number,
                label: 'Loan Period',
                layout: 'default',
                name: 'interestOnlyLoanPeriodTable',
                unit: 'years',
                condition: [
                  {
                    field: 'mortgageInterestOnlyTable',
                    isLocal: true,
                    value: true,
                  },
                ],
                before: true,
              },
              {
                type: FormUIOptions.YesNo,
                label: 'Interest paid monthly?',
                layout: 'default',
                name: 'interestOnlyMonthlyTable',
                description:
                  'Is the interest on your loan paid monthly or at the end of the loan period?',
                defaultValue: false,
                condition: [
                  {
                    field: 'mortgageInterestOnlyTable',
                    isLocal: true,
                    value: true,
                  },
                ],
                before: true,
              },
              {
                type: FormUIOptions.Number,
                label: 'Amortization',
                layout: 'default',
                name: 'amortizationTable',
                description: 'Mortgage period',
                unit: 'years',
                condition: [
                  {
                    field: 'mortgageInterestOnlyTable',
                    isLocal: true,
                    value: false,
                  },
                ],
                before: true,
              },
              {
                type: FormUIOptions.Calculated,
                label: 'Monthly Payment',
                layout: 'default',
                name: 'monthlyPaymentTable',
                unit: '$',
                derivedFrom: [
                  'mortgageAmountTable',
                  'interestRateTable',
                  'mortgageInterestOnlyTable',
                  'interestOnlyMonthlyTable',
                  'amortizationTable',
                ],
                calcFunction: (
                  mortgageAmountTable,
                  interestRateTable,
                  mortgageInterestOnlyTable,
                  interestOnlyMonthlyTable,
                  amortizationTable
                ) => {
                  return mortgageFormSectionCalculator(
                    mortgageAmountTable,
                    interestRateTable,
                    mortgageInterestOnlyTable,
                    interestOnlyMonthlyTable,
                    amortizationTable
                  );
                },
                before: true,
              },
            ],
          },
          {
            type: FormUIOptions.CalculatedSection,
            name: 'newMortgageCalculations',
            groupStart: true,
            schema: [
              {
                type: FormUIOptions.Calculated,
                label: 'New Mortgage Amount',
                layout: 'default',
                groupStart: true,
                name: 'newMortgageTotal',
                unit: '$',
                derivedFrom: [
                  'loanToValueRatio',
                  'newMarketValue',
                  'cmhcPremiumNew',
                  'shouldCalculateCMHCNew',
                  'newMortgageInterestOnly',
                  'BRRRRAdditionalFinancing',
                  'propertyInCanada',
                ],
                calcFunction: (
                  loanToValueRatio,
                  newMarketValue,
                  cmhcPremiumNew,
                  shouldCalculateCMHCNew,
                  newMortgageInterestOnly,
                  BRRRRAdditionalFinancing,
                  propertyInCanada
                ) => {
                  let firstMortgageAmount = 0;

                  if (isValue(loanToValueRatio, newMarketValue)) {
                    firstMortgageAmount =
                      (loanToValueRatio * getNumberFromString(newMarketValue)) /
                      100;
                  } else {
                    return null;
                  }

                  if (
                    cmhcPremiumNew &&
                    shouldCalculateCMHCNew &&
                    !newMortgageInterestOnly &&
                    propertyInCanada !== 0
                  ) {
                    firstMortgageAmount += cmhcPremiumNew;
                  }

                  const otherFinancingAmount = Array.isArray(
                    BRRRRAdditionalFinancing
                  )
                    ? BRRRRAdditionalFinancing.reduce((acc, curr) => {
                        return curr.mortgageAmountTable
                          ? acc + convertUnitMoney(curr.mortgageAmountTable)
                          : acc;
                      }, 0)
                    : 0;

                  return firstMortgageAmount + otherFinancingAmount;
                },
                formula: (newMortgageAmount) => [`-`, `${newMortgageAmount}`],
              },
              {
                type: FormUIOptions.Calculated,
                label: 'New Mortgage Payment',
                layout: 'default',
                name: 'newMonthlyPayment',
                per: 'month',
                unit: '$',
                derivedFrom: [
                  'newMortgageInterestOnly',
                  'newInterestRate',
                  'loanToValueRatio',
                  'newMarketValue',
                  'newAmortization',
                  'newInterestOnlyMonthly',
                  'cmhcPremiumNew',
                  'shouldCalculateCMHCNew',
                  'BRRRRAdditionalFinancing',
                  'propertyInCanada',
                ],
                calcFunction: (
                  newMortgageInterestOnly,
                  newInterestRate,
                  loanToValueRatio,
                  newMarketValue,
                  newAmortization,
                  newInterestOnlyMonthly,
                  cmhcPremiumNew,
                  shouldCalculateCMHCNew,
                  BRRRRAdditionalFinancing,
                  propertyInCanada
                ) => {
                  let mortgageAmount = null;

                  if (isValue(newMarketValue, loanToValueRatio)) {
                    mortgageAmount =
                      (loanToValueRatio * getNumberFromString(newMarketValue)) /
                      100;
                  } else {
                    return null;
                  }

                  if (
                    cmhcPremiumNew &&
                    shouldCalculateCMHCNew &&
                    !newMortgageInterestOnly &&
                    propertyInCanada !== 0
                  ) {
                    mortgageAmount += cmhcPremiumNew;
                  }

                  const monthlyPayment = mortgageFormSectionCalculator(
                    mortgageAmount,
                    newInterestRate,
                    newMortgageInterestOnly,
                    newInterestOnlyMonthly,
                    newAmortization
                  );

                  const additionalMonthlyPayments = Array.isArray(
                    BRRRRAdditionalFinancing
                  )
                    ? BRRRRAdditionalFinancing.reduce((acc, curr) => {
                        return curr.monthlyPaymentTable
                          ? acc + convertUnitMoney(curr.monthlyPaymentTable)
                          : acc;
                      }, 0)
                    : 0;

                  return monthlyPayment + additionalMonthlyPayments;
                },
              },
              {
                type: FormUIOptions.Calculated,
                label: 'Cash Flow',
                total: true,
                layout: 'default',
                name: 'newMonthlyPreTaxCashFlow',
                unit: '$',
                per: 'month',
                derivedFrom: [
                  'totalGrossRentalIncome',
                  'totalOtherIncome',
                  'totalOperatingExpenses',
                  'newMonthlyPayment',
                  'isRehabFinanced',
                  'rehabCostFinancing',
                  'payOffOnRefinanceRehab',
                  'monthlyPaymentRehab',
                ],
                calcFunction: (
                  totalGrossRentalIncome,
                  totalOtherIncome,
                  totalOperatingExpenses,
                  newMonthlyPayment,
                  isRehabFinanced,
                  rehabCostFinancing,
                  payOffOnRefinanceRehab,
                  monthlyPaymentRehab
                ) => {
                  if (isValue(newMonthlyPayment)) {
                    let rehabFinancingPayments = 0;
                    if (isRehabFinanced) {
                      rehabFinancingPayments += Array.isArray(
                        rehabCostFinancing
                      )
                        ? rehabCostFinancing.reduce((acc, curr) => {
                            return !curr.payOffOnRefinanceTable
                              ? acc + convertUnitMoney(curr.monthlyPaymentTable)
                              : acc;
                          }, 0)
                        : 0;

                      rehabFinancingPayments += payOffOnRefinanceRehab
                        ? 0
                        : monthlyPaymentRehab;
                    }

                    const totalMonthlyIncome =
                      totalGrossRentalIncome + totalOtherIncome;
                    const result =
                      totalMonthlyIncome -
                      totalOperatingExpenses -
                      newMonthlyPayment -
                      rehabFinancingPayments;

                    return result;
                  } else {
                    return null;
                  }
                },
              },

              {
                type: FormUIOptions.Calculated,
                label: 'Debt Coverage Ratio',
                name: 'debtCoverageRatio',
                tooltipHeading: 'Debt Coverage Ratio (DCR, DSCR)',
                decimalPlaces: 2,
                description:
                  'The ratio between the net operating income and the monthly debt service.',
                tooltipFormula: [
                  'Debt Coverage Ratio',
                  '=',
                  'Net Operating Income',
                  '/',
                  'Debt Service',
                ],
                condition: {
                  field: 'cashPurchase',
                  value: false,
                },
                layout: 'default',
                decimals: 2,
                emphasize: true,
                red: 1,
                green: 1.1,
                derivedFrom: ['netMonthlyIncome', 'newMonthlyPaymentWithRehab'],
                calcFunction: (monthlyNOI, monthlyDebtService) => {
                  if (!isValue(monthlyNOI, monthlyDebtService)) {
                    return null;
                  }

                  return monthlyNOI / monthlyDebtService;
                },
              },
            ],
          },
        ],
      },
      {
        sectionLabel: 'Refinancing Costs',
        sectionDescription: 'Enter the costs associated with refinancing',
        sectionValue: 'refinancingCostsItemized',
        schema: [
          {
            type: FormUIOptions.TableInline,
            layout: 'default',
            itemName: 'Refinancing Cost',
            label: '',
            name: 'refinancingCosts',
            defaultItems: [
              {
                costName: 'Appraisal',
                costAmount: '',
              },
              {
                costName: 'Lender Fees',
                costAmount: '',
              },
              {
                costName: 'Closing Costs',
                costAmount: '',
              },
            ],
            tableSchema: [
              {
                type: FormUIOptions.Text,
                label: 'Name',
                layout: 'default',
                name: 'costName',
              },
              {
                type: FormUIOptions.Money,
                label: 'Amount',
                layout: 'default',
                name: 'costAmount',
              },
            ],
          },
          {
            type: FormUIOptions.CalculatedSection,
            name: 'refinancingCostsSummary',
            groupStart: true,
            schema: [
              {
                type: FormUIOptions.Calculated,
                label: 'Total',
                layout: 'default',

                name: 'totalRefinancingCosts',
                unit: '$',
                derivedFrom: ['refinancingCosts'],
                calcFunction: (refinancingCosts) =>
                  getTableSum(refinancingCosts, 'costAmount'),
              },
            ],
          },
        ],
      },
    ],
  },
  calculations: {
    title: 'Scenario Summary',
    description: 'Updated metrics based on your scenario',
    schema: [
      {
        sectionType: 'diagram',
        sectionLabel: 'BRRRR Summary',
        sectionValue: 'rehabSummary',
        schema: [
          {
            type: FormUIOptions.Money,
            label: 'Purchase Price',
            name: 'purchasePrice',
            doNotCalculate: true,
          },
          {
            type: FormUIOptions.Money,
            label: 'Market Value (Est)',
            name: 'marketPurchasePrice',
            doNotCalculate: true,
          },
          {
            type: FormUIOptions.Number,
            label: 'Rehab Length',
            name: 'rehabLength',
            unit: ' months',
            decimalPlaces: 0,
          },
          {
            type: FormUIOptions.Money,
            label: 'New Projected Value',
            name: 'newMarketValue',
          },
          // {
          //   type: FormUIOptions.Money,
          //   label: 'Potential Value (Best Case)',
          //   name: 'newMarketValuePotential',
          //   condition: {
          //     field: 'enterTotalRentalIncome',
          //     value: [false, undefined, ''],
          //   },
          // },
          {
            type: FormUIOptions.Money,
            label: 'Equity Difference',
            dynamic: true,
            name: 'overUnderPayment',
            doNotCalculate: true,
          },
          {
            type: FormUIOptions.Money,
            label: 'Value Increase',
            name: 'valueIncreaseActual',
            derivedFrom: ['newMarketValue', 'marketPurchasePrice'],
            calcFunction: (newMarketValue, purchasePrice) => {
              return newMarketValue - purchasePrice;
            },
            dynamic: true,
          },
          // {
          //   type: FormUIOptions.Money,
          //   label: 'Value Increase (Potential)',
          //   name: 'valueIncreasePotential',
          //   derivedFrom: ['newMarketValuePotential', 'marketPurchasePrice'],
          //   calcFunction: (newMarketValuePotential, purchasePrice) => {
          //     return newMarketValuePotential - purchasePrice;
          //   },
          //   condition: {
          //     field: 'enterTotalRentalIncome',
          //     value: [false, undefined, ''],
          //   },
          // },
        ],
      },
      {
        sectionType: 'twoColumn',
        leftSchema: {
          sectionLabel: 'Value Summary',
          schema: [
            {
              type: FormUIOptions.Money,
              label: 'Market Purchase Price (Est)',
              name: 'marketPurchasePrice',
              tooltipHeading: 'Market Purchase Price',
              description:
                'The estimated market value of the property at the time of purchase',
              doNotCalculate: true,
            },
            {
              type: FormUIOptions.Number,
              label: 'Rehab Length',
              name: 'rehabLength',
              unit: ' months',
              decimalPlaces: 0,
            },
            {
              type: FormUIOptions.Money,
              label: 'New Value',
              name: 'newMarketValue',
              tooltipHeading: 'New Value',
              description:
                'The estimated market value of the property after the rehab',
              emphasize: true,
            },
            // {
            //   type: FormUIOptions.Money,
            //   label: 'New Value (Potential)',
            //   name: 'newMarketValuePotential',
            //   tooltipHeading: 'New Value (Potential)',
            //   description:
            //     'The estimated market value of the property if all units are rented at market rates',
            //   small: true,
            //   condition: {
            //     field: 'enterTotalRentalIncome',
            //     value: [false, undefined, ''],
            //   },
            // },
            {
              type: FormUIOptions.Money,
              label: 'Value Increase',
              name: 'valueIncreaseActual',
              emphasize: true,
              dynamic: true,
              groupStart: true,
              total: true,
              derivedFrom: ['newMarketValue', 'marketPurchasePrice'],
              calcFunction: (newMarketValue, purchasePrice) => {
                return newMarketValue - purchasePrice;
              },
              displayPercentFrom: [
                'valueIncreaseActual',
                'marketPurchasePrice',
              ],
              displayPercentFunction: (forcedAppreciation, purchasePrice) => {
                return (
                  Math.round((forcedAppreciation / purchasePrice) * 100 * 100) /
                  100
                );
              },
              formula: (forcedAppreciation) => [`=`, `${forcedAppreciation}`],
            },
            // {
            //   type: FormUIOptions.Money,
            //   label: 'Value Increase (Potential)',
            //   name: 'valueIncreasePotential',
            //   small: true,
            //   groupEnd: true,
            //   derivedFrom: ['newMarketValuePotential', 'marketPurchasePrice'],
            //   calcFunction: (newMarketValue, purchasePrice) => {
            //     return newMarketValue - purchasePrice;
            //   },
            //   displayPercentFrom: [
            //     'valueIncreasePotential',
            //     'marketPurchasePrice',
            //   ],
            //   displayPercentFunction: (forcedAppreciation, purchasePrice) => {
            //     return (
            //       Math.round((forcedAppreciation / purchasePrice) * 100 * 100) /
            //       100
            //     );
            //   },
            //   formula: (forcedAppreciation) => [`=`, `${forcedAppreciation}`],
            //   condition: {
            //     field: 'enterTotalRentalIncome',
            //     value: [false, undefined, ''],
            //   },
            // },
          ],
        },
        rightSchema: {
          sectionLabel: 'Equity',
          schema: [
            {
              type: FormUIOptions.Money,
              label: 'New Value',
              emphasize: true,
              name: 'newMarketValue',
            },
            // {
            //   type: FormUIOptions.Money,
            //   label: 'New Value (Potential)',
            //   name: 'newMarketValuePotential',
            //   small: true,
            //   condition: {
            //     field: 'enterTotalRentalIncome',
            //     value: [false, undefined, ''],
            //   },
            // },
            {
              type: FormUIOptions.Money,
              label: 'New Mortgage',
              tooltipHaeading: 'Mortgage Amount',
              description: 'Total mortgage amount for the property',
              name: 'newMortgageTotal',
              formula: (totalFinancingAmount) => ['-', totalFinancingAmount],
            },
            {
              type: FormUIOptions.Money,
              label: 'Rehab Financing',
              tooltipHeading: 'Rehab Financing',
              description:
                'Total financing amount for the rehab, not including financing paid off on refinance',
              name: 'rehabFinancingTotalBRRRR',
              derivedFrom: [
                'rehabCostFinancing',
                'payOffOnRefinanceRehab',
                'mortgageAmountRehab',
                'isRehabFinanced',
              ],
              calcFunction: (
                rehabCostFinancing,
                payOffOnRefinanceRehab,
                mortgageAmountRehab,
                isRehabFinanced
              ) => {
                if (!isRehabFinanced) {
                  return 0;
                }

                const rehabAdditionalLoan = Array.isArray(rehabCostFinancing)
                  ? rehabCostFinancing.reduce((acc, curr) => {
                      return !curr.payOffOnRefinanceTable
                        ? acc + convertUnitMoney(curr.mortgageAmountTable)
                        : acc;
                    }, 0)
                  : 0;

                const rehabOutstandingLoan = payOffOnRefinanceRehab
                  ? 0
                  : mortgageAmountRehab;
                return rehabOutstandingLoan + rehabAdditionalLoan;
              },
              formula: (rehabFinancingAmount) => [
                `-`,
                `${rehabFinancingAmount}`,
              ],
            },
            {
              type: FormUIOptions.Money,
              label: 'Equity',
              name: 'equityInProperty',
              emphasize: true,
              dynamic: true,
              total: true,
              derivedFrom: [
                'newMarketValue',
                'newMortgageTotal',
                'rehabFinancingTotalBRRRR',
              ],
              calcFunction: (
                newMarketValue,
                totalMortgageAmount,
                rehabFinancingTotalBRRRR
              ) => {
                return (
                  newMarketValue -
                  getSum(totalMortgageAmount, rehabFinancingTotalBRRRR)
                );
              },
              formula: (newEquity) => [`=`, `${newEquity}`],
            },
            // {
            //   type: FormUIOptions.Money,
            //   label: 'Equity (Potential)',
            //   name: 'equityInPropertyPotential',
            //   small: true,
            //   groupEnd: true,
            //   derivedFrom: [
            //     'newMarketValuePotential',
            //     'newMortgageTotal',
            //     'rehabFinancingTotalBRRRR',
            //   ],
            //   calcFunction: (
            //     newMarketValue,
            //     totalMortgageAmount,
            //     rehabFinancingTotalBRRRR
            //   ) => {
            //     return (
            //       newMarketValue -
            //       getSum(totalMortgageAmount, rehabFinancingTotalBRRRR)
            //     );
            //   },
            //   formula: (newEquity) => [`=`, `${newEquity}`],
            //   condition: {
            //     field: 'enterTotalRentalIncome',
            //     value: [false, undefined, ''],
            //   },
            // },
          ],
        },
      },

      {
        sectionType: 'twoColumn',
        leftSchema: {
          sectionLabel: 'Financing Details',
          schema: [
            {
              type: FormUIOptions.Money,
              label: 'New Value',
              name: 'newMarketValue',
              unit: '$',
            },
            {
              type: FormUIOptions.Money,
              label: 'New Mortgage',
              name: 'newMortgageTotal',
              dropDown: true,
              formula: (newMortgageTotal) => [`-`, `${newMortgageTotal}`],
            },

            {
              type: FormUIOptions.Money,
              label: 'Mortgage Amount',
              layout: 'default',
              name: 'newMortgageAmount',
              dropDownDependency: 'newMortgageTotal',
              unit: '$',
              derivedFrom: ['loanToValueRatio', 'newMarketValue'],
              calcFunction: (loanToValueRatio, newMarketValue) => {
                return (
                  (loanToValueRatio * getNumberFromString(newMarketValue)) / 100
                );
              },
            },
            {
              type: FormUIOptions.Money,
              label: 'Insurance Premium',
              layout: 'default',
              name: 'cmhcPremiumNew',
              dropDownDependency: 'newMortgageTotal',
              condition: [
                {
                  field: 'shouldCalculateCMHCNew',
                  value: true,
                },
                {
                  field: 'propertyInCanada',
                  value: [undefined, null, 1, ''],
                },
              ],
            },
            {
              type: FormUIOptions.Number,
              label: 'Interest Rate',
              layout: 'default',
              name: 'newInterestRate',
              unit: '%',
              dropDownDependency: 'newMortgageTotal',
            },
            {
              type: FormUIOptions.YesNo,
              label: 'Interest-Only',
              layout: 'default',
              name: 'newMortgageInterestOnly',
              dropDownDependency: 'newMortgageTotal',
            },
            {
              type: FormUIOptions.YesNo,
              label: 'Interest paid monthly?',
              layout: 'default',
              name: 'newInterestOnlyMonthly',
              dropDownDependency: 'newMortgageTotal',
              condition: {
                field: 'newMortgageInterestOnly',
                value: true,
              },
            },
            {
              type: FormUIOptions.Number,
              label: 'Loan Period',
              layout: 'default',
              name: 'newInterestOnlyLoanPeriod',
              unit: 'years',
              dropDownDependency: 'newMortgageTotal',
              condition: {
                field: 'newMortgageInterestOnly',
                value: true,
              },
            },
            {
              type: FormUIOptions.Number,
              label: 'Amortization',
              layout: 'default',
              name: 'newAmortization',
              unit: 'years',
              dropDownDependency: 'newMortgageTotal',
              condition: {
                field: 'newMortgageInterestOnly',
                value: false,
              },
            },
            {
              type: FormUIOptions.Number,
              label: 'Loan-to-Value',
              layout: 'default',
              name: 'loanToValueRatio',
              unit: '%',
              dropDownDependency: 'newMortgageTotal',
            },
            {
              type: FormUIOptions.Money,
              label: 'Monthly Payment',
              layout: 'default',
              name: 'newFirstMonthlyPayment',
              unit: '$',
              groupEnd: true,
              dropDownDependency: 'newMortgageTotal',
              derivedFrom: [
                'newInterestRate',
                'newMortgageAmount',
                'newAmortization',
                'newMortgageInterestOnly',
                'newInterestOnlyMonthly',
              ],
              calcFunction: (
                interestRate,
                mortgageAmount,
                amortization,
                mortgageInterestOnly,
                interestOnlyMonthly
              ) => {
                return mortgageFormSectionCalculator(
                  mortgageAmount,
                  interestRate,
                  mortgageInterestOnly,
                  interestOnlyMonthly,
                  amortization
                );
              },
            },
            {
              type: FormUIOptions.Table,
              label: 'Additional Financing',
              name: 'BRRRRAdditionalFinancing',
              dropDownDependency: 'newMortgageTotal',
              schema: [
                {
                  type: FormUIOptions.Text,
                  name: 'loanNameTable',
                  label: 'Loan Name',
                  dropDownDependency: 'newMortgageTotal',
                },
                {
                  type: FormUIOptions.Money,
                  label: 'Loan Amount',
                  layout: 'default',
                  name: 'mortgageAmountTable',
                  unit: '$',
                  dropDownDependency: 'newMortgageTotal',
                },
                {
                  type: FormUIOptions.YesNo,
                  label: 'Interest-Only',
                  layout: 'default',
                  name: 'mortgageInterestOnlyTable',
                  dropDownDependency: 'newMortgageTotal',
                  condition: {
                    field: 'mortgageInterestOnlyTable',
                    value: true,
                  },
                },
                {
                  type: FormUIOptions.Number,
                  label: 'Interest Rate',
                  layout: 'default',
                  name: 'interestRateTable',
                  dropDownDependency: 'newMortgageTotal',
                  unit: '%',
                },
                {
                  type: FormUIOptions.YesNo,
                  label: 'Interest paid monthly?',
                  layout: 'default',
                  name: 'mortgageInterestOnlyTable',
                  dropDownDependency: 'newMortgageTotal',
                  condition: {
                    field: 'mortgageInterestOnlyTable',
                    value: true,
                  },
                },
                {
                  type: FormUIOptions.Number,
                  label: 'Loan Period',
                  layout: 'default',
                  unit: 'years',
                  name: 'interestOnlyLoanPeriodTable',
                  dropDownDependency: 'newMortgageTotal',
                  condition: {
                    field: 'mortgageInterestOnlyTable',
                    value: true,
                  },
                },
                {
                  type: FormUIOptions.Number,
                  label: 'Amortization',
                  layout: 'default',
                  name: 'amortizationTable',
                  unit: 'years',
                  dropDownDependency: 'newMortgageTotal',
                  condition: {
                    field: 'mortgageInterestOnlyTable',
                    value: false,
                  },
                },
                {
                  type: FormUIOptions.Number,
                  label: 'Loan To Value',
                  layout: 'default',
                  name: 'loanToValueTable',
                  unit: 'years',
                  dropDownDependency: 'newMortgageTotal',
                  mainValue: 'percent',
                  displayPercentFrom: ['mortgageAmountTable', 'newMarketValue'],
                  displayPercentFunction: (
                    mortgageAmountTable,
                    purchasePrice
                  ) => {
                    return (
                      yearlyPercentFromYearlyValue(mortgageAmountTable, [
                        purchasePrice,
                      ]) || ' -'
                    );
                  },
                },
                {
                  type: FormUIOptions.Money,
                  label: 'Monthly Payment',
                  layout: 'default',
                  name: 'monthlyPaymentTable',
                  groupEnd: true,
                  unit: '$',
                  dropDownDependency: 'newMortgageTotal',
                },
              ],
            },
            {
              type: FormUIOptions.Money,
              label: 'Rehab Financing',
              name: 'rehabFinancingTotalBRRRR',
              dropDown: true,
              condition: {
                field: 'isRehabFinanced',
                value: true,
              },
              formula: (rehabFinancingTotalBRRRR) => [
                `-`,
                `${rehabFinancingTotalBRRRR}`,
              ],
            },
            {
              type: FormUIOptions.Money,
              label: 'Loan Amount',
              layout: 'default',
              name: 'mortgageAmountRehab',
              unit: '$',
              dropDownDependency: 'rehabFinancingTotalBRRRR',
            },
            {
              type: FormUIOptions.YesNo,
              label: 'Pay off on refinance?',
              name: 'payOffOnRefinanceRehab',
              dropDownDependency: 'rehabFinancingTotalBRRRR',
            },
            {
              type: FormUIOptions.YesNo,
              label: 'Interest-Only',
              layout: 'default',
              name: 'mortgageInterestOnlyRehab',
              dropDownDependency: 'rehabFinancingTotalBRRRR',
              condition: {
                field: 'mortgageInterestOnlyTable',
                value: true,
              },
            },
            {
              type: FormUIOptions.Number,
              label: 'Interest Rate',
              layout: 'default',
              name: 'interestRateRehab',
              dropDownDependency: 'rehabFinancingTotalBRRRR',
              unit: '%',
            },
            {
              type: FormUIOptions.YesNo,
              label: 'Interest paid monthly?',
              layout: 'default',
              name: 'mortgageInterestOnlyRehab',
              dropDownDependency: 'rehabFinancingTotalBRRRR',
              condition: {
                field: 'mortgageInterestOnlyRehab',
                value: true,
              },
            },
            {
              type: FormUIOptions.Number,
              label: 'Loan Period',
              layout: 'default',
              unit: 'years',
              name: 'interestOnlyLoanPeriodRehab',
              dropDownDependency: 'rehabFinancingTotalBRRRR',
              condition: {
                field: 'mortgageInterestOnlyRehab',
                value: true,
              },
            },
            {
              type: FormUIOptions.Number,
              label: 'Amortization',
              layout: 'default',
              name: 'amortizationRehab',
              unit: 'years',
              dropDownDependency: 'rehabFinancingTotalBRRRR',
              condition: {
                field: 'mortgageInterestOnlyRehab',
                value: false,
              },
            },
            {
              type: FormUIOptions.Number,
              label: 'Loan To Value',
              layout: 'default',
              name: 'loanToValueRehab',
              unit: 'years',
              dropDownDependency: 'rehabFinancingTotalBRRRR',
              mainValue: 'percent',
              displayPercentFrom: ['mortgageAmountRehab', 'purchasePrice'],
              displayPercentFunction: (mortgageAmountTable, purchasePrice) => {
                return (
                  yearlyPercentFromYearlyValue(mortgageAmountTable, [
                    purchasePrice,
                  ]) || ' -'
                );
              },
            },
            {
              type: FormUIOptions.Money,
              label: 'Monthly Payment',
              layout: 'default',
              name: 'monthlyPaymentRehab',
              derivedFrom: [
                'mortgageAmountRehab',
                'interestRateRehab',
                'mortgageInterestOnlyRehab',
                'interestOnlyMonthlyRehab',
                'amortizationRehab',
              ],
              calcFunction: (
                mortgageAmountRehab,
                interestRateRehab,
                mortgageInterestOnlyRehab,
                interestOnlyMonthlyRehab,
                amortizationRehab
              ) => {
                return mortgageFormSectionCalculator(
                  mortgageAmountRehab,
                  interestRateRehab,
                  mortgageInterestOnlyRehab,
                  interestOnlyMonthlyRehab,
                  amortizationRehab
                );
              },
              groupEnd: true,
              unit: '$',
              dropDownDependency: 'rehabFinancingTotalBRRRR',
            },
            {
              type: FormUIOptions.Table,
              label: 'Additional Financing',
              name: 'rehabCostFinancing',
              dropDownDependency: 'rehabFinancingTotalBRRRR',
              schema: [
                {
                  type: FormUIOptions.Text,
                  name: 'loanNameTable',
                  label: 'Loan Name',
                  dropDownDependency: 'rehabFinancingTotalBRRRR',
                },
                {
                  type: FormUIOptions.YesNo,
                  label: 'Payoff on Refinance',
                  layout: 'default',
                  name: 'payOffOnRefinanceTable',
                  dropDownDependency: 'rehabFinancingTotalBRRRR',
                  derivedFrom: ['payOffOnRefinanceTable'],
                  calcFunction: (payOffOnRefinanceTable) => {
                    return payOffOnRefinanceTable ? true : false;
                  },
                },
                {
                  type: FormUIOptions.Money,
                  label: 'Loan Amount',
                  layout: 'default',
                  name: 'mortgageAmountTable',
                  unit: '$',
                  dropDownDependency: 'rehabFinancingTotalBRRRR',
                },
                {
                  type: FormUIOptions.YesNo,
                  label: 'Interest-Only',
                  layout: 'default',
                  name: 'mortgageInterestOnlyTable',
                  dropDownDependency: 'rehabFinancingTotalBRRRR',
                  condition: {
                    field: 'mortgageInterestOnlyTable',
                    value: true,
                  },
                },
                {
                  type: FormUIOptions.Number,
                  label: 'Interest Rate',
                  layout: 'default',
                  name: 'interestRateTable',
                  dropDownDependency: 'rehabFinancingTotalBRRRR',
                  unit: '%',
                },
                {
                  type: FormUIOptions.YesNo,
                  label: 'Interest paid monthly?',
                  layout: 'default',
                  name: 'mortgageInterestOnlyTable',
                  dropDownDependency: 'rehabFinancingTotalBRRRR',
                  condition: {
                    field: 'mortgageInterestOnlyTable',
                    value: true,
                  },
                },
                {
                  type: FormUIOptions.Number,
                  label: 'Loan Period',
                  layout: 'default',
                  unit: 'years',
                  name: 'interestOnlyLoanPeriodTable',
                  dropDownDependency: 'rehabFinancingTotalBRRRR',
                  condition: {
                    field: 'mortgageInterestOnlyTable',
                    value: true,
                  },
                },
                {
                  type: FormUIOptions.Number,
                  label: 'Amortization',
                  layout: 'default',
                  name: 'amortizationTable',
                  unit: 'years',
                  dropDownDependency: 'rehabFinancingTotalBRRRR',
                  condition: {
                    field: 'mortgageInterestOnlyTable',
                    value: false,
                  },
                },
                {
                  type: FormUIOptions.Number,
                  label: 'Loan To Value',
                  layout: 'default',
                  name: 'loanToValueTable',
                  unit: 'years',
                  dropDownDependency: 'rehabFinancingTotalBRRRR',
                  mainValue: 'percent',
                  displayPercentFrom: ['mortgageAmountTable', 'newMarketValue'],
                  displayPercentFunction: (
                    mortgageAmountTable,
                    purchasePrice
                  ) => {
                    return (
                      yearlyPercentFromYearlyValue(mortgageAmountTable, [
                        purchasePrice,
                      ]) || ' -'
                    );
                  },
                },
                {
                  type: FormUIOptions.Money,
                  label: 'Monthly Payment',
                  layout: 'default',
                  name: 'monthlyPaymentTable',
                  groupEnd: true,
                  unit: '$',
                  dropDownDependency: 'rehabFinancingTotalBRRRR',
                },
              ],
            },
            {
              type: FormUIOptions.Money,
              label: 'New Equity',
              name: 'equityInProperty',
              total: true,
              emphasize: true,
              derivedFrom: [
                'newMortgageTotal',
                'rehabFinancingTotalBRRRR',
                'isRehabFinanced',
                'newMarketValue',
              ],
              calcFunction: (
                newMortgageTotal,
                rehabFinancingTotalBRRRR,
                isRehabFinanced,
                newMarketValue
              ) => {
                if (!isRehabFinanced) {
                  return newMarketValue - newMortgageTotal;
                } else {
                  return (
                    newMarketValue -
                    newMortgageTotal -
                    (rehabFinancingTotalBRRRR || 0)
                  );
                }
              },
              formula: (equityInProperty) => [`=`, `${equityInProperty}`],
            },
            {
              type: FormUIOptions.Money,
              label: 'New Mortgage',
              name: 'newFinancingTotal',
              groupStart: true,
              derivedFrom: ['newMortgageTotal'],
              calcFunction: (newMortgageTotal) => {
                return newMortgageTotal;
              },
            },
            {
              type: FormUIOptions.Money,
              label: 'Original Mortgage',
              name: 'totalMortgageAmountNoCMHC',
              tooltipHeading: 'Original Mortgage',
              description:
                'The original mortgage amount not including insurance premium',
              unit: '$',
              derivedFrom: [
                'totalMortgageAmount',
                'cmhcPremium',
                'shouldCalculateCMHC',
                'mortgageInterestOnly',
                'propertyInCanada',
              ],
              calcFunction: (
                totalMortgageAmount,
                cmhcPremium,
                shouldCalculateCMHC,
                mortgageInterestOnly,
                propertyInCanada
              ) => {
                if (
                  !shouldCalculateCMHC ||
                  mortgageInterestOnly ||
                  propertyInCanada === 0
                ) {
                  return totalMortgageAmount;
                } else {
                  return totalMortgageAmount - (cmhcPremium || 0);
                }
              },
              formula: (purchasePrice) => [`-`, `${purchasePrice}`],
            },

            {
              type: FormUIOptions.Money,
              label: 'Insurance Premium',
              name: 'cmhcPremium',
              doNotCalculate: true,
              dropDownDependency: 'purchasePrice',
              condition: [
                {
                  field: 'shouldCalculateCMHC',
                  value: true,
                },
                { field: 'mortgageInterestOnly', value: false },
                { field: 'propertyInCanada', value: [undefined, null, 1, ''] },
              ],
              formula: (cmhcPremium) => [`-`, `${cmhcPremium}`],
            },
            {
              type: FormUIOptions.Money,
              label: 'Insurance Premium',
              name: 'cmhcPremiumNew',
              unit: '$',
              formula: (cmhcPremiumNew) => [`-`, `${cmhcPremiumNew}`],
              condition: [
                {
                  field: 'shouldCalculateCMHCNew',
                  value: true,
                },
                {
                  field: 'propertyInCanada',
                  value: [undefined, null, 1, ''],
                },
              ],
            },
            {
              type: FormUIOptions.Money,
              label: 'Cash Invested',
              name: 'totalCashInvestedWithDownPayment',
              dropDown: true,
              unit: '$',
              derivedFrom: [
                'totalRehabCosts',
                'rehabFinancingTotalBRRRR',
                'totalCashRequiredToClose',
                'totalCarryingCosts',
                'downPaymentAmount',
                'totalRefinancingCosts',
                'enterTotalRentalIncome',
              ],
              calcFunction: (
                totalRehabCosts,
                rehabFinancingTotalBRRRR,
                totalCashRequiredToClose,
                totalCarryingCosts,
                downPaymentAmount,
                totalRefinancingCosts,
                enterTotalRentalIncome
              ) => {
                const carryingCosts = enterTotalRentalIncome
                  ? 0
                  : totalCarryingCosts;

                return getSum(
                  totalRehabCosts - (rehabFinancingTotalBRRRR || 0),
                  totalCashRequiredToClose,
                  carryingCosts,
                  downPaymentAmount || 0,
                  totalRefinancingCosts || 0
                );
              },
              formula: (totalCashInvested) => [`-`, `${totalCashInvested}`],
            },
            {
              type: FormUIOptions.Money,
              label: 'Down Payment',
              name: 'downPaymentAmount',
              doNotCalculate: true,
              dropDownDependency: 'totalCashInvestedWithDownPayment',
            },
            {
              type: FormUIOptions.Money,
              label: 'Rehab Costs Cash',
              name: 'rehabCostUnfinancedBRRRR',
              dropDownDependency: 'totalCashInvestedWithDownPayment',
              derivedFrom: ['totalRehabCosts', 'rehabFinancingTotalBRRRR'],
              calcFunction: (totalRehabCosts, rehabFinancingTotalBRRRR) => {
                return totalRehabCosts - (rehabFinancingTotalBRRRR || 0);
              },
              unit: '$',
            },
            {
              type: FormUIOptions.Money,
              label: 'Closing Cost(s) (Purchase)',
              doNotCalculate: true,
              dropDownDependency: 'totalCashInvestedWithDownPayment',
              layout: 'default',
              name: 'totalCashRequiredToClose',
              unit: '$',
            },
            {
              type: FormUIOptions.Money,
              label: 'Refinancing Costs',
              dropDownDependency: 'totalCashInvestedWithDownPayment',
              layout: 'default',
              name: 'totalRefinancingCosts',
            },
            {
              type: FormUIOptions.Money,
              label: 'Carrying Costs',
              dropDownDependency: 'totalCashInvestedWithDownPayment',
              layout: 'default',
              description:
                'Total holding/carrying costs (including mortgage payments) for the rehab period',
              name: 'totalCarryingCosts',
              unit: '$',
              condition: {
                field: 'enterTotalRentalIncome',
                value: [false, null, undefined, ''],
              },
            },
            {
              type: FormUIOptions.Money,
              label: ['Cash / Profit', 'Cash Left In'],
              name: 'cashOut',
              emphasize: true,
              total: true,
              dynamic: true,
              absoluteValue: true,
              unit: '$',
              formula: (cashOut) => [`=`, `${cashOut}`],
              derivedFrom: [
                'newFinancingTotal',
                'totalCashInvestedWithDownPayment',
                'totalMortgageAmountNoCMHC',
                'cmhcPremiumNew',
                'shouldCalculateCMHCNew',
                'propertyInCanada',
              ],
              calcFunction: (
                newFinancingTotal,
                totalCashInvestedWithDownPayment,
                totalMortgageAmountNoCMHC,
                cmhcPremiumNew,
                shouldCalculateCMHCNew,
                propertyInCanada
              ) => {
                if (
                  isValue(
                    newFinancingTotal,
                    totalCashInvestedWithDownPayment,
                    totalMortgageAmountNoCMHC
                  )
                ) {
                  let cashOut;
                  cashOut =
                    newFinancingTotal -
                    getSum(
                      totalCashInvestedWithDownPayment,
                      totalMortgageAmountNoCMHC
                    );

                  if (
                    shouldCalculateCMHCNew &&
                    cmhcPremiumNew &&
                    cashOut &&
                    propertyInCanada !== 0
                  ) {
                    return cashOut - cmhcPremiumNew;
                  }

                  return cashOut;
                }
                return null;
              },
            },
          ],
        },
        rightSchema: {
          sectionLabel: 'Cash Flow',
          sectionSubLabel: 'Monthly',
          annualValue: true,
          schema: [
            {
              type: FormUIOptions.Money,
              label: 'Rental Income',
              layout: 'default',
              name: 'totalRentalIncome',
              tooltipHeading: 'Rental Income',
              description: 'Rental income across all units',
              unit: '$',
              derivedFrom: ['rentalIncomePreVacancy'],
              calcFunction: (rentalIncomePreVacancy) => {
                return rentalIncomePreVacancy;
              },
            },
            {
              type: FormUIOptions.Money,
              label: 'Vacancy',
              layout: 'default',
              name: 'vacancy',
              unit: '$',
              derivedFrom: ['vacancyDeduction'],
              calcFunction: (vacancyDeduction) => {
                const vacancy = convertUnitMoney(vacancyDeduction);
                if (isValue(vacancy)) {
                  return Math.round(vacancy * 100) / 100;
                } else {
                  return 0;
                }
              },
              formula: (vacancy) => ['-', `${vacancy}`],
              displayPercentFrom: ['vacancyDeduction', 'totalRentalIncome'],
              displayPercentFunction: (vacancyDeduction, totalRentalIncome) => {
                if (isValue(vacancyDeduction) && isValue(totalRentalIncome)) {
                  return Math.round(
                    (vacancyDeduction / totalRentalIncome) * 100
                  );
                } else {
                  return 0;
                }
              },
            },
            {
              type: FormUIOptions.Money,
              label: 'Additional Income',
              layout: 'default',
              name: 'totalOtherIncome',
              dropDown: true,
              unit: '$',
              formula: (otherIncome) => ['+', `${otherIncome}`],
            },
            {
              type: FormUIOptions.Table,
              label: '',
              name: 'otherPropertyIncome',
              dropDownDependency: 'totalOtherIncome',
              condition: [
                {
                  field: 'cashPurchase',
                  value: false,
                },
                {
                  field: 'enterTotalOtherIncome',
                  value: [false, undefined, ''],
                },
              ],
              schema: [
                {
                  type: FormUIOptions.Text,
                  name: 'otherIncomeName',
                  label: 'Other Income',
                  dropDownDependency: 'totalOtherIncome',
                },
                {
                  type: FormUIOptions.UnitMoney,
                  label: 'Amount',
                  layout: 'default',
                  name: 'otherIncomeAmount',
                  unit: '$',
                  dropDownDependency: 'totalOtherIncome',
                },
              ],
            },
            {
              type: FormUIOptions.Money,
              label: 'Gross Income (GOI)',
              layout: 'default',
              name: 'monthlyGOI',
              total: true,
              emphasize: true,
              tooltipHeading: 'Gross Operating Income',
              description:
                'Rental and other income across all units adjusted for vacancy rate',
              unit: '$',
              derivedFrom: [
                'totalGrossRentalIncome',
                'totalAdditionalIncomeBuyAndHold',
              ],
              calcFunction: (
                totalGrossRentalIncome,
                totalAdditionalIncomeBuyAndHold
              ) => {
                return getSum(
                  totalGrossRentalIncome,
                  totalAdditionalIncomeBuyAndHold
                );
              },
              formula: (monthlyGOI) => ['=', `${monthlyGOI}`],
            },
            {
              type: FormUIOptions.Money,
              label: 'Total Operating Expenses',
              layout: 'default',
              name: 'totalMonthlyOperatingExpenses',
              dropDown: true,
              unit: '$',
              derivedFrom: ['totalOperatingExpenses'],
              calcFunction: (totalOperatingExpenses) => totalOperatingExpenses,
              formula: (totalMonthlyOperatingExpenses) => [
                '-',
                `${totalMonthlyOperatingExpenses}`,
              ],
            },
            {
              type: FormUIOptions.Table,
              label: '',
              name: 'operatingExpenses',
              dropDownDependency: 'totalMonthlyOperatingExpenses',
              condition: [
                {
                  field: 'cashPurchase',
                  value: false,
                },
                {
                  field: 'enterTotalExpenses',
                  value: [false, undefined, ''],
                },
              ],
              schema: [
                {
                  type: FormUIOptions.Text,
                  name: 'expenseName',
                  label: 'Expense',
                  dropDownDependency: 'totalMonthlyOperatingExpenses',
                },
                {
                  type: FormUIOptions.UnitMoney,
                  derivedFrom: 'rentalIncomePreVacancy',
                  label: 'Amount',
                  layout: 'default',
                  name: 'expenseAmount',
                  unit: '$',
                  dropDownDependency: 'totalMonthlyOperatingExpenses',
                },
              ],
            },
            {
              type: FormUIOptions.Money,
              label: 'Net Income (NOI)',
              layout: 'default',
              name: 'netMonthlyIncome',
              total: true,
              emphasize: true,
              unit: '$',
              derivedFrom: ['monthlyGOI', 'totalMonthlyOperatingExpenses'],
              calcFunction: (monthlyGOI, totalMonthlyOperatingExpenses) => {
                if (
                  isValue(monthlyGOI) &&
                  isValue(totalMonthlyOperatingExpenses)
                ) {
                  return (
                    Math.round(
                      monthlyGOI * 100 - totalMonthlyOperatingExpenses * 100
                    ) / 100
                  );
                } else {
                  return null;
                }
              },
              formula: (netMonthlyIncome) => ['=', `${netMonthlyIncome}`],
            },
            {
              type: FormUIOptions.Money,
              label: 'Loan Payments',
              layout: 'default',
              name: 'newMonthlyPaymentWithRehab',
              unit: '$',
              derivedFrom: [
                'newMonthlyPayment',
                'rehabCostFinancing',
                'monthlyPaymentRehab',
                'payOffOnRefinanceRehab',
                'isRehabFinanced',
              ],
              calcFunction: (
                newMonthlyPayment,
                rehabCostFinancing,
                monthlyPaymentRehab,
                payOffOnRefinanceRehab,
                isRehabFinanced
              ) => {
                if (!isRehabFinanced) {
                  return newMonthlyPayment;
                }

                const rehabAdditionalPayments = Array.isArray(
                  rehabCostFinancing
                )
                  ? rehabCostFinancing.reduce((acc, curr) => {
                      return !curr.payOffOnRefinanceTable
                        ? acc + convertUnitMoney(curr.monthlyPaymentTable)
                        : acc;
                    }, 0)
                  : 0;

                const rehabPayment = payOffOnRefinanceRehab
                  ? 0
                  : monthlyPaymentRehab;

                return (
                  newMonthlyPayment + rehabPayment + rehabAdditionalPayments
                );
              },
              formula: (newMortgageAmount) => ['-', `${newMortgageAmount}`],
            },
            {
              type: FormUIOptions.Money,
              label: 'Cash Flow',
              total: true,
              emphasize: true,
              dynamic: true,
              layout: 'default',
              name: 'newMonthlyPreTaxCashFlow',
              unit: '$',
              formula: (newMonthlyPreTaxCashFlow) => [
                '=',
                `${newMonthlyPreTaxCashFlow}`,
              ],
            },
          ],
        },
      },
      //removing this section for now
      // {
      //   sectionType: 'chart',
      //   layoutClasses: 'order-[-400]',
      //   sectionLabel: 'Financial Ratios',
      //   schema: [
      //     {
      //       type: FormUIOptions.Number,
      //       label: 'Rent to Value',
      //       name: 'newRentToValue',
      //       unit: '%',
      //       layout: 'default',
      //       tooltipHeading: 'Rent to Value',
      //       description: 'Annual rent as a percentage of the property value.',
      //       tooltipFormula: [
      //         'Rent to Value',
      //         '=',
      //         'Annual Rent',
      //         '/',
      //         'Market Value',
      //       ],
      //       derivedFrom: ['monthlyGOI', 'newMarketValue'],
      //       calcFunction: (monthlyGOI, newMarketValue) => {
      //         if (isValue(monthlyGOI, newMarketValue)) {
      //           return (
      //             Math.round(
      //               (getNumberFromString(monthlyGOI) * 12 * 10000) /
      //                 newMarketValue
      //             ) / 100
      //           );
      //         } else {
      //           return null;
      //         }
      //       },
      //     },
      //     {
      //       type: FormUIOptions.Number,
      //       label: 'Gross Rent Multiplier',
      //       layout: 'default',
      //       name: 'newGrossRentMultiplier',
      //       tooltipHeading: 'Gross Rent Multiplier',
      //       description:
      //         'The ratio between the property price and the gross rental income.',
      //       tooltipFormula: [
      //         'Gross Rent Multiplier',
      //         '=',
      //         'Market Value',
      //         '/',
      //         'Annual Rent',
      //       ],
      //       unit: '',
      //       derivedFrom: ['monthlyGOI', 'newMarketValue'],
      //       calcFunction: (monthlyGOI, newMarketValue) => {
      //         if (isValue(monthlyGOI, newMarketValue)) {
      //           const monthlyGOIValue = getNumberFromString(monthlyGOI);
      //           const purchasePriceValue = getNumberFromString(newMarketValue);

      //           const yearlyGOI = monthlyGOIValue * 12;
      //           const result = purchasePriceValue / yearlyGOI;
      //           return Math.round(result * 100) / 100;
      //         } else {
      //           return null;
      //         }
      //       },
      //     },
      //     {
      //       type: FormUIOptions.Number,
      //       label: 'Equity Multiple',
      //       layout: 'default',
      //       name: 'newEquityMultiple',
      //       tooltipHeading: 'Equity Multiple',
      //       description:
      //         'The ratio between the total equity and the total cash invested.',
      //       tooltipFormula: [
      //         'Equity Multiple',
      //         '=',
      //         '(Total Equity',
      //         '+',
      //         'Cumulative Cash Flow)',
      //         '/',
      //         'Total Cash Invested',
      //       ],
      //       unit: '',
      //       derivedFrom: [
      //         'equityInProperty',
      //         'newMonthlyPreTaxCashFlow',
      //         'cashOut',
      //       ],
      //       calcFunction: (
      //         equityInProperty,
      //         newMonthlyPreTaxCashFlow,
      //         cashOut
      //       ) => {
      //         if (!isValue(equityInProperty, cashOut)) {
      //           return null;
      //         }

      //         return yearlyPercentFromYearlyValue(
      //           equityInProperty + newMonthlyPreTaxCashFlow * 12,
      //           [cashOut > 0 ? 0 : -cashOut]
      //         );
      //       },
      //     },
      //     {
      //       type: FormUIOptions.Number,
      //       label: 'Break Even Ratio',
      //       tooltipHeading: 'Break Even Ratio',
      //       description:
      //         'The % of units that need to be occupied to break even.',
      //       tooltipFormula: [
      //         'Break Even Ratio',
      //         '=',
      //         '(Yearly Expenses',
      //         '+',
      //         'Yearly Loan Payment)',
      //         '/',
      //         'Yearly Gross Rent',
      //       ],
      //       name: 'breakEvenRatio',
      //       unit: '%',
      //       layout: 'default',
      //       derivedFrom: [
      //         'monthlyGOI',
      //         'newMonthlyPaymentWithRehab',
      //         'totalMonthlyOperatingExpenses',
      //       ],
      //       calcFunction: (
      //         monthlyGOI,
      //         newMonthlyPaymentWithRehab,
      //         totalMonthlyOperatingExpenses
      //       ) => {
      //         const yearlyLoanPayment = newMonthlyPaymentWithRehab * 12;
      //         const totalExpenses = totalMonthlyOperatingExpenses * 12;
      //         const totalIncome = monthlyGOI * 12;

      //         return ((totalExpenses + yearlyLoanPayment) / totalIncome) * 100;
      //       },
      //     },
      //     {
      //       type: FormUIOptions.Number,
      //       label: 'Debt Coverage Ratio',
      //       name: 'debtCoverageRatio',
      //       tooltipHeading: 'Debt Coverage Ratio',
      //       description:
      //         'The ratio between the net operating income and the monthly debt service.',
      //       tooltipFormula: [
      //         'Debt Coverage Ratio',
      //         '=',
      //         'Net Operating Income',
      //         '/',
      //         'Debt Service',
      //       ],
      //       layout: 'default',
      //       dynamic: true,
      //       red: 1,
      //       green: 1.1,
      //       derivedFrom: ['netMonthlyIncome', 'newMonthlyPaymentWithRehab'],
      //       calcFunction: (monthlyNOI, monthlyDebtService) => {
      //         if (!isValue(monthlyNOI, monthlyDebtService)) {
      //           return null;
      //         }

      //         return monthlyNOI / monthlyDebtService;
      //       },
      //     },
      //     {
      //       type: FormUIOptions.Number,
      //       label: 'Debt Yield',
      //       name: 'debtYield',
      //       layout: 'default',
      //       tooltipHeading: 'Debt Yield',
      //       description:
      //         'The ratio between the net operating income and the total loan amount.',
      //       tooltipFormula: [
      //         'Debt Yield',
      //         '=',
      //         'Yearly Net Operating Income',
      //         '/',
      //         'Total Loan Amount',
      //       ],
      //       unit: '%',
      //       derivedFrom: ['netMonthlyIncome', 'newMortgageTotal'],
      //       calcFunction: (monthlyNOI, totalMortgageAmount) => {
      //         if (!isValue(monthlyNOI, totalMortgageAmount)) {
      //           return null;
      //         }

      //         return ((monthlyNOI * 12) / totalMortgageAmount) * 100;
      //       },
      //     },
      //   ],
      //   chartSchema: {
      //     title: 'New Total ROI',
      //     type: 'bar',
      //     schema: [
      //       {
      //         type: FormUIOptions.Number,
      //         label: 'Cash Flow',
      //         name: 'newMonthlyPreTaxCashFlow',
      //         multiplier: 12,
      //         unit: '$',
      //       },
      //       {
      //         type: FormUIOptions.Number,
      //         label: 'Principal Recapture',
      //         name: 'newYearOnePrincipalReduction',
      //         unit: '$',
      //       },
      //       {
      //         type: FormUIOptions.Number,
      //         label: 'Passive Appreciation',
      //         name: 'marketAppreciationAmount',
      //         unit: '$',
      //       },
      //       {
      //         type: FormUIOptions.Number,
      //         label: 'Forced Appreciation',
      //         name: 'forcedAppreciation',
      //         unit: '$',
      //       },
      //     ],
      //   },
      // },
      {
        sectionType: 'twoColumn',
        layoutClasses: 'order-first',
        leftSchema: {
          sectionValue: 'investmentMetricsBRRRR',
          sectionLabel: 'Investment Metrics',
          percentageValue: true,
          annualValue: true,
          schema: [
            {
              type: FormUIOptions.Money,
              label: 'Cash on Cash',
              tooltipHeading: 'Cash on Cash',
              dynamic: true,
              description:
                'Annual pre-tax cash flow expressed as a percentage of the cash left in the property after refinancing, or zero if there is profit after refinancing.',
              isAnnual: true,
              mainValue: 'percent',
              showMonthly: false,
              showAnnual: false,
              name: 'newMonthlyPreTaxCashFlow',
              unit: '$',
              displayPercentFrom: ['newMonthlyPreTaxCashFlow', 'cashOut'],
              displayPercentFunction: (newMonthlyPreTaxCashFlow, cashOut) => {
                return yearlyPercentFromYearlyValue(
                  newMonthlyPreTaxCashFlow * 12,
                  [cashOut > 0 ? 0 : -cashOut]
                );
              },
            },
            {
              type: FormUIOptions.Money,
              label: 'Principal Recapture',
              layout: 'default',
              name: 'newYearOnePrincipalReduction',
              mainValue: 'percent',
              description:
                'Shows how much of the borrowed amount is paid back in the first 12 months, as a percentage of the cash left in the property after refinancing.',
              tooltipHeading: 'Principal Recapture',
              unit: '$',
              isAnnual: true,
              derivedFrom: [
                'newInterestRate',
                'newMortgageAmount',
                'newFirstMonthlyPayment',
                'BRRRRAdditionalFinancing',
              ],
              calcFunction: (
                interestRate,
                mortgageAmount,
                monthlyPayment,
                BRRRRAdditionalFinancing
              ) => {
                if (
                  isValue(interestRate, mortgageAmount, monthlyPayment || null)
                ) {
                  const firstMortgageOutstandingAmount =
                    mortgagePrincipalOutstanding(
                      interestRate,
                      getNumberFromString(mortgageAmount),
                      getNumberFromString(monthlyPayment),
                      12
                    );

                  const firstMortgageReduction =
                    Math.round(
                      getNumberFromString(mortgageAmount) * 100 -
                        firstMortgageOutstandingAmount * 100
                    ) / 100;

                  let additionalMortgageReduction = 0;

                  if (Array.isArray(BRRRRAdditionalFinancing)) {
                    BRRRRAdditionalFinancing.forEach((loan) => {
                      const {
                        mortgageAmountTable,
                        monthlyPaymentTable,
                        interestRateTable,
                      } = loan;
                      if (
                        !isValue(
                          mortgageAmountTable,
                          monthlyPaymentTable || null,
                          interestRateTable
                        )
                      ) {
                        return;
                      }

                      const outstandingAmount = mortgagePrincipalOutstanding(
                        interestRateTable,
                        getNumberFromString(mortgageAmountTable),
                        getNumberFromString(monthlyPaymentTable),
                        12
                      );

                      additionalMortgageReduction +=
                        Math.round(
                          getNumberFromString(mortgageAmountTable) * 100 -
                            outstandingAmount * 100
                        ) / 100;
                    });
                  }

                  return firstMortgageReduction + additionalMortgageReduction;
                } else {
                  return null;
                }
              },
              displayPercentFrom: ['newYearOnePrincipalReduction', 'cashOut'],
              displayPercentFunction: (
                newYearOnePrincipalReduction,
                cashOut
              ) => {
                return yearlyPercentFromYearlyValue(
                  newYearOnePrincipalReduction,
                  [cashOut > 0 ? 0 : -cashOut]
                );
              },
            },
            {
              type: FormUIOptions.Money,
              label: 'Passive Appreciation',
              layout: 'default',
              name: 'marketAppreciationAmount',
              unit: '$',
              mainValue: 'percent',
              isAnnual: true,
              displayPercentFrom: ['marketAppreciationAmount', 'cashOut'],
              displayPercentFunction: (marketAppreciationAmount, cashOut) => {
                return yearlyPercentFromYearlyValue(marketAppreciationAmount, [
                  cashOut > 0 ? 0 : -cashOut,
                ]);
              },
            },
            {
              type: FormUIOptions.Money,
              label: 'Forced Appreciation',
              name: 'forcedAppreciation',
              mainValue: 'percent',
              dynamic: true,
              isAnnual: true,
              showMonthly: false,
              tooltipHeading: 'Forced Appreciation',
              tooltipFormula: [
                'Forced Appreciation',
                '=',
                'New Market Value',
                '-',
                'Initial Market Value',
                '/',
                '(Total Cash Invested',
                '+',
                'Rehab Financing)',
              ],
              derivedFrom: ['newMarketValue', 'marketPurchasePrice'],
              calcFunction: (newMarketValue, marketPurchasePrice) => {
                return newMarketValue - marketPurchasePrice;
              },
              description:
                'Increase in the market value of the property. This is the difference between the new market value and the purchase price.',
              displayPercentFrom: [
                'forcedAppreciation',
                'totalCashInvestedWithDownPayment',
                'rehabFinancingTotalBRRRR',
              ],
              displayPercentFunction: (
                forcedAppreciation,
                totalCashInvestedWithDownPayment,
                rehabFinancingTotalBRRRR
              ) => {
                return yearlyPercentFromYearlyValue(forcedAppreciation, [
                  totalCashInvestedWithDownPayment,
                  rehabFinancingTotalBRRRR,
                ]);
              },
            },
            {
              type: FormUIOptions.Money,
              label: 'New Total ROI',
              isAnnual: true,
              groupEnd: true,
              emphasize: true,
              layout: 'default',
              mainValue: 'percent',
              name: 'newTotalRoiYear1',
              tooltipHeading: 'New Total ROI',
              description:
                'New ROI After Refinancing. The percentage is based on the cash left in the property after refinancing. If you have no cash left in the property, the ROI will be infinite.',
              tooltipFormula: [
                'New Total ROI',
                '=',
                '(Cash on Cash',
                '+',
                'Principal Recapture',
                '+',
                'Passive Appreciation',
                '+',
                'Forced Appreciation)',
              ],
              derivedFrom: [
                'newYearOnePrincipalReduction',
                'marketAppreciationAmount',
                'forcedAppreciation',
                'newMonthlyPreTaxCashFlow',
              ],
              calcFunction: (
                newYearOnePrincipalReduction,
                marketAppreciationAmount,
                forcedAppreciation,
                newMonthlyPreTaxCashFlow
              ) => {
                const totalROI = getSum(
                  newYearOnePrincipalReduction,
                  marketAppreciationAmount,
                  forcedAppreciation,
                  newMonthlyPreTaxCashFlow * 12
                );
                return totalROI;
              },
              displayPercentFrom: [
                'forcedAppreciation',
                'marketAppreciationAmount',
                'newYearOnePrincipalReduction',
                'newMonthlyPreTaxCashFlow',
                'cashOut',
                'totalCashInvestedWithDownPayment',
                'rehabFinancingTotalBRRRR',
              ],
              displayPercentFunction: (
                forcedAppreciation,
                marketAppreciationAmount,
                newYearOnePrincipalReduction,
                newMonthlyPreTaxCashFlow,
                cashOut,
                totalCashInvestedWithDownPayment,
                rehabFinancingTotalBRRRR
              ) => {
                const firstPercent = yearlyPercentFromYearlyValue(
                  forcedAppreciation,
                  [totalCashInvestedWithDownPayment, rehabFinancingTotalBRRRR]
                );
                const secondPercent = yearlyPercentFromYearlyValue(
                  marketAppreciationAmount +
                    newYearOnePrincipalReduction +
                    newMonthlyPreTaxCashFlow * 12,
                  [cashOut > 0 ? 0 : -cashOut]
                );
                return (
                  Math.round(
                    (Number(firstPercent) + Number(secondPercent)) * 100
                  ) / 100
                );
              },
            },

            {
              type: FormUIOptions.Number,
              label: 'Cap Rate (Purchase)',
              layout: 'default',
              name: 'capRate',
              mainValue: 'percent',
              showMonthly: false,
              showAnnual: false,
              unit: '%',
              isAnnual: true,
              groupStart: true,
              derivedFrom: [
                'monthlyGOI',
                'totalMonthlyOperatingExpenses',
                'purchasePrice',
              ],
              calcFunction: (
                monthlyGOI,
                totalMonthlyOperatingExpenses,
                purchasePrice
              ) => {
                const netMonthlyIncome =
                  monthlyGOI - totalMonthlyOperatingExpenses;

                return (
                  Math.round(
                    (getNumberFromString(netMonthlyIncome) * 12 * 10000) /
                      purchasePrice
                  ) / 100
                );
              },
            },
            {
              type: FormUIOptions.Number,
              label: 'Cap Rate (Market)',
              description: 'Cap rate based on the new market value',
              tooltipHeading: 'Cap Rate (Market)',
              tooltipFormula: [
                'Cap Rate (Market)',
                '=',
                'Annual Net Operating Income',
                '/',
                'New Market Value',
              ],
              layout: 'default',
              name: 'marketCapRate',
              mainValue: 'percent',
              showMonthly: false,
              showAnnual: false,
              unit: '%',
              isAnnual: true,
              derivedFrom: [
                'monthlyGOI',
                'totalMonthlyOperatingExpenses',
                'newMarketValue',
              ],
              calcFunction: (
                monthlyGOI,
                totalMonthlyOperatingExpenses,
                newMarketValue
              ) => {
                const netMonthlyIncome =
                  monthlyGOI - totalMonthlyOperatingExpenses;

                return (
                  Math.round(
                    (getNumberFromString(netMonthlyIncome) * 12 * 10000) /
                      newMarketValue
                  ) / 100
                );
              },
            },
            ///the following field is not included in the UI, but is used to retrieve a value needed for the key metrics section, since the key metrics section is designed to only display pre-existing values (somewhat of a hack method)
            {
              type: FormUIOptions.Number,
              label: 'Value Increase',
              name: 'valueIncreaseActual',
              tooltipHeading: 'Property Value Increase',
              description:
                'Increase in the market value of the property. This is another way to calculate the forced appreciation, using the initial market value to calculate the percentage increase.',
              tooltipFormula: [
                'Value Increase',
                '=',
                '(New Market Value',
                '-',
                'Market Purchase Price)',
                '/',
                'Market Purchase Price',
              ],
              unit: '$',
              mainValue: 'percent',
              isAnnual: true,
              showMonthly: false,
              dynamic: true,
              displayPercentFrom: [
                'valueIncreaseActual',
                'marketPurchasePrice',
              ],
              displayPercentFunction: (forcedAppreciation, purchasePrice) => {
                return (
                  Math.round((forcedAppreciation / purchasePrice) * 100 * 100) /
                  100
                );
              },
            },
            {
              type: FormUIOptions.Money,
              label: 'Monthly NOI',
              tooltipHeading: 'Net Operating Income',
              showAnnualBrackets: true,
              description:
                'Net Operating Income. The total income from the property after deducting all operating expenses, but before deducting mortgage payments and taxes.',
              layout: 'default',
              name: 'netMonthlyIncome',
              per: 'month',
              unit: '$',
            },
            ///the following field is not included in the UI, but is used to retrieve a value needed for the key metrics section, since the key metrics section is designed to only display pre-existing values (somewhat of a hack method)
            {
              type: FormUIOptions.Money,
              label: 'Investment Recovered',
              name: 'investmentRecovered',
              unit: '$',
              derivedFrom: ['cashOut', 'totalCashInvestedWithDownPayment'],
              calcFunction: (cashOut, totalCashInvestedWithDownPayment) => {
                if (cashOut >= 0) {
                  return totalCashInvestedWithDownPayment;
                } else {
                  let recovered = totalCashInvestedWithDownPayment + cashOut;
                  if (recovered < 0) {
                    recovered = 0;
                  }
                  return recovered;
                }
              },
              condition: {
                field: 'fieldThatDoesNotExist',
                value: 'valueThatDoesNotExist',
              },
            },
            {
              type: FormUIOptions.Money,
              unit: '$',
              label: '',
              name: 'BRRRRCostOnlyCash',
              derivedFrom: [
                'rehabCostUnfinancedBRRRR',
                'totalCarryingCosts',
                'totalRefinancingCosts',
                'enterTotalRentalIncome',
              ],
              calcFunction: (
                rehabCostUnfinanced,
                totalCarryingCosts,
                totalRefinancingCosts,
                enterTotalRentalIncome
              ) => {
                if (enterTotalRentalIncome) {
                  return (
                    (rehabCostUnfinanced || 0) + (totalRefinancingCosts || 0)
                  );
                }
                return (
                  (totalCarryingCosts || 0) +
                  (rehabCostUnfinanced || 0) +
                  (totalRefinancingCosts || 0)
                );
              },
              condition: {
                field: 'fieldThatDoesNotExist',
                value: 'valueThatDoesNotExist',
              },
            },
          ],
        },
        rightSchema: {
          sectionLabel: 'Financial Ratios',
          sectionValue: 'FinancialRatios',
          schema: [
            {
              type: FormUIOptions.Number,
              label: 'Rent to Value',
              name: 'newRentToValue',
              unit: '%',
              layout: 'default',
              tooltipHeading: 'Rent to Value',
              description: 'Annual rent as a percentage of the property value.',
              tooltipFormula: [
                'Rent to Value',
                '=',
                'Annual Rent',
                '/',
                'Market Value',
              ],
              derivedFrom: ['monthlyGOI', 'newMarketValue'],
              calcFunction: (monthlyGOI, newMarketValue) => {
                if (isValue(monthlyGOI, newMarketValue)) {
                  return (
                    Math.round(
                      (getNumberFromString(monthlyGOI) * 12 * 10000) /
                        newMarketValue
                    ) / 100
                  );
                } else {
                  return null;
                }
              },
            },
            {
              type: FormUIOptions.Number,
              label: 'Gross Rent Multiplier',
              layout: 'default',
              name: 'newGrossRentMultiplier',
              tooltipHeading: 'Gross Rent Multiplier',
              description:
                'The ratio between the property price and the gross rental income.',
              tooltipFormula: [
                'Gross Rent Multiplier',
                '=',
                'Market Value',
                '/',
                'Annual Rent',
              ],
              unit: '',
              derivedFrom: ['monthlyGOI', 'newMarketValue'],
              calcFunction: (monthlyGOI, newMarketValue) => {
                if (isValue(monthlyGOI, newMarketValue)) {
                  const monthlyGOIValue = getNumberFromString(monthlyGOI);
                  const purchasePriceValue =
                    getNumberFromString(newMarketValue);

                  const yearlyGOI = monthlyGOIValue * 12;
                  const result = purchasePriceValue / yearlyGOI;
                  return Math.round(result * 100) / 100;
                } else {
                  return null;
                }
              },
            },
            {
              type: FormUIOptions.Number,
              label: 'Equity Multiple',
              layout: 'default',
              name: 'newEquityMultiple',
              tooltipHeading: 'Equity Multiple',
              description:
                'The ratio between the total equity and the total cash invested.',
              tooltipFormula: [
                'Equity Multiple',
                '=',
                '(Total Equity',
                '+',
                'Cumulative Cash Flow)',
                '/',
                'Total Cash Invested',
              ],
              unit: '',
              derivedFrom: [
                'equityInProperty',
                'newMonthlyPreTaxCashFlow',
                'cashOut',
              ],
              calcFunction: (
                equityInProperty,
                newMonthlyPreTaxCashFlow,
                cashOut
              ) => {
                if (!isValue(equityInProperty, cashOut)) {
                  return null;
                }

                return yearlyPercentFromYearlyValue(
                  equityInProperty + newMonthlyPreTaxCashFlow * 12,
                  [cashOut > 0 ? 0 : -cashOut]
                );
              },
            },
            {
              type: FormUIOptions.Number,
              label: 'Break Even Ratio',
              tooltipHeading: 'Break Even Ratio',
              description:
                'The % of units that need to be occupied to break even.',
              tooltipFormula: [
                'Break Even Ratio',
                '=',
                '(Yearly Expenses',
                '+',
                'Yearly Loan Payment)',
                '/',
                'Yearly Gross Rent',
              ],
              name: 'breakEvenRatio',
              unit: '%',
              layout: 'default',
              derivedFrom: [
                'monthlyGOI',
                'newMonthlyPaymentWithRehab',
                'totalMonthlyOperatingExpenses',
              ],
              calcFunction: (
                monthlyGOI,
                newMonthlyPaymentWithRehab,
                totalMonthlyOperatingExpenses
              ) => {
                const yearlyLoanPayment = newMonthlyPaymentWithRehab * 12;
                const totalExpenses = totalMonthlyOperatingExpenses * 12;
                const totalIncome = monthlyGOI * 12;

                return (
                  ((totalExpenses + yearlyLoanPayment) / totalIncome) * 100
                );
              },
            },
            {
              type: FormUIOptions.Number,
              label: 'Debt Coverage Ratio',
              name: 'debtCoverageRatio',
              tooltipHeading: 'Debt Coverage Ratio',
              description:
                'The ratio between the net operating income and the monthly debt service.',
              tooltipFormula: [
                'Debt Coverage Ratio',
                '=',
                'Net Operating Income',
                '/',
                'Debt Service',
              ],
              layout: 'default',
              dynamic: true,
              red: 1,
              green: 1.1,
              derivedFrom: ['netMonthlyIncome', 'newMonthlyPaymentWithRehab'],
              calcFunction: (monthlyNOI, monthlyDebtService) => {
                if (!isValue(monthlyNOI, monthlyDebtService)) {
                  return null;
                }

                return monthlyNOI / monthlyDebtService;
              },
            },
            {
              type: FormUIOptions.Number,
              label: 'Debt Yield',
              name: 'debtYield',
              layout: 'default',
              tooltipHeading: 'Debt Yield',
              description:
                'The ratio between the net operating income and the total loan amount.',
              tooltipFormula: [
                'Debt Yield',
                '=',
                'Yearly Net Operating Income',
                '/',
                'Total Loan Amount',
              ],
              unit: '%',
              derivedFrom: ['netMonthlyIncome', 'newMortgageTotal'],
              calcFunction: (monthlyNOI, totalMortgageAmount) => {
                if (!isValue(monthlyNOI, totalMortgageAmount)) {
                  return null;
                }

                return ((monthlyNOI * 12) / totalMortgageAmount) * 100;
              },
            },
          ],
        },
      },
    ],
  },
  simpleCalculations: {
    title: 'Scenario Summary',
    description: 'Updated metrics based on your scenario',
    schema: [
      {
        sectionType: 'twoColumn',
        leftSchema: {
          sectionValue: 'investmentMetricsBRRRR',
          sectionLabel: 'Investment Metrics',
          percentageValue: true,
          annualValue: true,
          schema: [
            {
              type: FormUIOptions.Money,
              label: 'Cash on Cash',
              tooltipHeading: 'Cash on Cash',
              dynamic: true,
              description:
                'Annual pre-tax cash flow expressed as a percentage of the cash left in the property after refinancing, or zero if there is profit after refinancing.',
              isAnnual: true,
              mainValue: 'percent',
              showMonthly: false,
              showAnnual: false,
              name: 'newMonthlyPreTaxCashFlow',
              unit: '$',
              displayPercentFrom: ['newMonthlyPreTaxCashFlow', 'cashOut'],
              displayPercentFunction: (newMonthlyPreTaxCashFlow, cashOut) => {
                return yearlyPercentFromYearlyValue(
                  newMonthlyPreTaxCashFlow * 12,
                  [cashOut > 0 ? 0 : -cashOut]
                );
              },
            },
            {
              type: FormUIOptions.Money,
              label: 'Principal Recapture',
              layout: 'default',
              name: 'newYearOnePrincipalReduction',
              mainValue: 'percent',
              description:
                'Shows how much of the borrowed amount is paid back in the first 12 months, as a percentage of the cash left in the property after refinancing.',
              tooltipHeading: 'Principal Recapture',
              unit: '$',
              isAnnual: true,
              derivedFrom: [
                'newInterestRate',
                'newMortgageAmount',
                'newFirstMonthlyPayment',
                'BRRRRAdditionalFinancing',
              ],
              calcFunction: (
                interestRate,
                mortgageAmount,
                monthlyPayment,
                BRRRRAdditionalFinancing
              ) => {
                if (
                  isValue(interestRate, mortgageAmount, monthlyPayment || null)
                ) {
                  const firstMortgageOutstandingAmount =
                    mortgagePrincipalOutstanding(
                      interestRate,
                      getNumberFromString(mortgageAmount),
                      getNumberFromString(monthlyPayment),
                      12
                    );

                  const firstMortgageReduction =
                    Math.round(
                      getNumberFromString(mortgageAmount) * 100 -
                        firstMortgageOutstandingAmount * 100
                    ) / 100;

                  let additionalMortgageReduction = 0;

                  if (Array.isArray(BRRRRAdditionalFinancing)) {
                    BRRRRAdditionalFinancing.forEach((loan) => {
                      const {
                        mortgageAmountTable,
                        monthlyPaymentTable,
                        interestRateTable,
                      } = loan;
                      if (
                        !isValue(
                          mortgageAmountTable,
                          monthlyPaymentTable || null,
                          interestRateTable
                        )
                      ) {
                        return;
                      }

                      const outstandingAmount = mortgagePrincipalOutstanding(
                        interestRateTable,
                        getNumberFromString(mortgageAmountTable),
                        getNumberFromString(monthlyPaymentTable),
                        12
                      );

                      additionalMortgageReduction +=
                        Math.round(
                          getNumberFromString(mortgageAmountTable) * 100 -
                            outstandingAmount * 100
                        ) / 100;
                    });
                  }

                  return firstMortgageReduction + additionalMortgageReduction;
                } else {
                  return null;
                }
              },
              displayPercentFrom: ['newYearOnePrincipalReduction', 'cashOut'],
              displayPercentFunction: (
                newYearOnePrincipalReduction,
                cashOut
              ) => {
                return yearlyPercentFromYearlyValue(
                  newYearOnePrincipalReduction,
                  [cashOut > 0 ? 0 : -cashOut]
                );
              },
            },
            {
              type: FormUIOptions.Money,
              label: 'Passive Appreciation',
              layout: 'default',
              name: 'marketAppreciationAmount',
              unit: '$',
              mainValue: 'percent',
              isAnnual: true,
              displayPercentFrom: ['marketAppreciationAmount', 'cashOut'],
              displayPercentFunction: (marketAppreciationAmount, cashOut) => {
                return yearlyPercentFromYearlyValue(marketAppreciationAmount, [
                  cashOut > 0 ? 0 : -cashOut,
                ]);
              },
            },
            {
              type: FormUIOptions.Money,
              label: 'Forced Appreciation',
              name: 'forcedAppreciation',
              mainValue: 'percent',
              dynamic: true,
              isAnnual: true,
              showMonthly: false,
              tooltipHeading: 'Forced Appreciation',
              tooltipFormula: [
                'Forced Appreciation',
                '=',
                'New Market Value',
                '-',
                'Initial Market Value',
                '/',
                '(Total Cash Invested',
                '+',
                'Rehab Financing)',
              ],
              derivedFrom: ['newMarketValue', 'marketPurchasePrice'],
              calcFunction: (newMarketValue, marketPurchasePrice) => {
                return newMarketValue - marketPurchasePrice;
              },
              description:
                'Increase in the market value of the property. This is the difference between the new market value and the purchase price.',
              displayPercentFrom: [
                'forcedAppreciation',
                'totalCashInvestedWithDownPayment',
                'rehabFinancingTotalBRRRR',
              ],
              displayPercentFunction: (
                forcedAppreciation,
                totalCashInvestedWithDownPayment,
                rehabFinancingTotalBRRRR
              ) => {
                return yearlyPercentFromYearlyValue(forcedAppreciation, [
                  totalCashInvestedWithDownPayment,
                  rehabFinancingTotalBRRRR,
                ]);
              },
            },
            {
              type: FormUIOptions.Money,
              label: 'New Total ROI',
              isAnnual: true,
              groupEnd: true,
              emphasize: true,
              layout: 'default',
              mainValue: 'percent',
              name: 'newTotalRoiYear1',
              tooltipHeading: 'New Total ROI',
              description:
                'New ROI After Refinancing. The percentage is based on the cash left in the property after refinancing. If you have no cash left in the property, the ROI will be infinite.',
              tooltipFormula: [
                'New Total ROI',
                '=',
                '(Cash on Cash',
                '+',
                'Principal Recapture',
                '+',
                'Passive Appreciation',
                '+',
                'Forced Appreciation)',
              ],
              derivedFrom: [
                'newYearOnePrincipalReduction',
                'marketAppreciationAmount',
                'forcedAppreciation',
                'newMonthlyPreTaxCashFlow',
              ],
              calcFunction: (
                newYearOnePrincipalReduction,
                marketAppreciationAmount,
                forcedAppreciation,
                newMonthlyPreTaxCashFlow
              ) => {
                const totalROI = getSum(
                  newYearOnePrincipalReduction,
                  marketAppreciationAmount,
                  forcedAppreciation,
                  newMonthlyPreTaxCashFlow * 12
                );
                return totalROI;
              },
              displayPercentFrom: [
                'forcedAppreciation',
                'marketAppreciationAmount',
                'newYearOnePrincipalReduction',
                'newMonthlyPreTaxCashFlow',
                'cashOut',
                'totalCashInvestedWithDownPayment',
                'rehabFinancingTotalBRRRR',
              ],
              displayPercentFunction: (
                forcedAppreciation,
                marketAppreciationAmount,
                newYearOnePrincipalReduction,
                newMonthlyPreTaxCashFlow,
                cashOut,
                totalCashInvestedWithDownPayment,
                rehabFinancingTotalBRRRR
              ) => {
                const firstPercent = yearlyPercentFromYearlyValue(
                  forcedAppreciation,
                  [totalCashInvestedWithDownPayment, rehabFinancingTotalBRRRR]
                );
                const secondPercent = yearlyPercentFromYearlyValue(
                  marketAppreciationAmount +
                    newYearOnePrincipalReduction +
                    newMonthlyPreTaxCashFlow * 12,
                  [cashOut > 0 ? 0 : -cashOut]
                );
                return (
                  Math.round(
                    (Number(firstPercent) + Number(secondPercent)) * 100
                  ) / 100
                );
              },
            },

            {
              type: FormUIOptions.Number,
              label: 'Cap Rate (Purchase)',
              layout: 'default',
              name: 'capRate',
              mainValue: 'percent',
              showMonthly: false,
              showAnnual: false,
              unit: '%',
              isAnnual: true,
              groupStart: true,
              derivedFrom: [
                'monthlyGOI',
                'totalMonthlyOperatingExpenses',
                'purchasePrice',
              ],
              calcFunction: (
                monthlyGOI,
                totalMonthlyOperatingExpenses,
                purchasePrice
              ) => {
                const netMonthlyIncome =
                  monthlyGOI - totalMonthlyOperatingExpenses;

                return (
                  Math.round(
                    (getNumberFromString(netMonthlyIncome) * 12 * 10000) /
                      purchasePrice
                  ) / 100
                );
              },
            },
            {
              type: FormUIOptions.Number,
              label: 'Cap Rate (Market)',
              description: 'Cap rate based on the new market value',
              tooltipHeading: 'Cap Rate (Market)',
              tooltipFormula: [
                'Cap Rate (Market)',
                '=',
                'Annual Net Operating Income',
                '/',
                'New Market Value',
              ],
              layout: 'default',
              name: 'marketCapRate',
              mainValue: 'percent',
              showMonthly: false,
              showAnnual: false,
              unit: '%',
              isAnnual: true,
              derivedFrom: [
                'monthlyGOI',
                'totalMonthlyOperatingExpenses',
                'newMarketValue',
              ],
              calcFunction: (
                monthlyGOI,
                totalMonthlyOperatingExpenses,
                newMarketValue
              ) => {
                const netMonthlyIncome =
                  monthlyGOI - totalMonthlyOperatingExpenses;

                return (
                  Math.round(
                    (getNumberFromString(netMonthlyIncome) * 12 * 10000) /
                      newMarketValue
                  ) / 100
                );
              },
            },
            ///the following field is not included in the UI, but is used to retrieve a value needed for the key metrics section, since the key metrics section is designed to only display pre-existing values (somewhat of a hack method)
            {
              type: FormUIOptions.Number,
              label: 'Value Increase',
              name: 'valueIncreaseActual',
              tooltipHeading: 'Property Value Increase',
              description:
                'Increase in the market value of the property. This is another way to calculate the forced appreciation, using the initial market value to calculate the percentage increase.',
              tooltipFormula: [
                'Value Increase',
                '=',
                '(New Market Value',
                '-',
                'Market Purchase Price)',
                '/',
                'Market Purchase Price',
              ],
              unit: '$',
              mainValue: 'percent',
              isAnnual: true,
              showMonthly: false,
              dynamic: true,
              displayPercentFrom: [
                'valueIncreaseActual',
                'marketPurchasePrice',
              ],
              displayPercentFunction: (forcedAppreciation, purchasePrice) => {
                return (
                  Math.round((forcedAppreciation / purchasePrice) * 100 * 100) /
                  100
                );
              },
            },
            {
              type: FormUIOptions.Money,
              label: 'Monthly NOI',
              tooltipHeading: 'Net Operating Income',
              showAnnualBrackets: true,
              description:
                'Net Operating Income. The total income from the property after deducting all operating expenses, but before deducting mortgage payments and taxes.',
              layout: 'default',
              name: 'netMonthlyIncome',
              per: 'month',
              unit: '$',
            },
            ///the following field is not included in the UI, but is used to retrieve a value needed for the key metrics section, since the key metrics section is designed to only display pre-existing values (somewhat of a hack method)
            {
              type: FormUIOptions.Money,
              label: 'Investment Recovered',
              name: 'investmentRecovered',
              unit: '$',
              derivedFrom: ['cashOut', 'totalCashInvestedWithDownPayment'],
              calcFunction: (cashOut, totalCashInvestedWithDownPayment) => {
                if (cashOut >= 0) {
                  return totalCashInvestedWithDownPayment;
                } else {
                  let recovered = totalCashInvestedWithDownPayment + cashOut;
                  if (recovered < 0) {
                    recovered = 0;
                  }
                  return recovered;
                }
              },
              condition: {
                field: 'fieldThatDoesNotExist',
                value: 'valueThatDoesNotExist',
              },
            },
            {
              type: FormUIOptions.Money,
              unit: '$',
              label: '',
              name: 'BRRRRCostOnlyCash',
              derivedFrom: [
                'rehabCostUnfinancedBRRRR',
                'totalCarryingCosts',
                'totalRefinancingCosts',
                'enterTotalRentalIncome',
              ],
              calcFunction: (
                rehabCostUnfinanced,
                totalCarryingCosts,
                totalRefinancingCosts,
                enterTotalRentalIncome
              ) => {
                if (enterTotalRentalIncome) {
                  return (
                    (rehabCostUnfinanced || 0) + (totalRefinancingCosts || 0)
                  );
                }
                return (
                  (totalCarryingCosts || 0) +
                  (rehabCostUnfinanced || 0) +
                  (totalRefinancingCosts || 0)
                );
              },
              condition: {
                field: 'fieldThatDoesNotExist',
                value: 'valueThatDoesNotExist',
              },
            },
          ],
        },
        rightSchema: {
          sectionLabel: 'Financial Ratios',
          sectionValue: 'FinancialRatios',
          schema: [
            {
              type: FormUIOptions.Number,
              label: 'Rent to Value',
              name: 'newRentToValue',
              unit: '%',
              layout: 'default',
              tooltipHeading: 'Rent to Value',
              description: 'Annual rent as a percentage of the property value.',
              tooltipFormula: [
                'Rent to Value',
                '=',
                'Annual Rent',
                '/',
                'Market Value',
              ],
              derivedFrom: ['monthlyGOI', 'newMarketValue'],
              calcFunction: (monthlyGOI, newMarketValue) => {
                if (isValue(monthlyGOI, newMarketValue)) {
                  return (
                    Math.round(
                      (getNumberFromString(monthlyGOI) * 12 * 10000) /
                        newMarketValue
                    ) / 100
                  );
                } else {
                  return null;
                }
              },
            },
            {
              type: FormUIOptions.Number,
              label: 'Gross Rent Multiplier',
              layout: 'default',
              name: 'newGrossRentMultiplier',
              tooltipHeading: 'Gross Rent Multiplier',
              description:
                'The ratio between the property price and the gross rental income.',
              tooltipFormula: [
                'Gross Rent Multiplier',
                '=',
                'Market Value',
                '/',
                'Annual Rent',
              ],
              unit: '',
              derivedFrom: ['monthlyGOI', 'newMarketValue'],
              calcFunction: (monthlyGOI, newMarketValue) => {
                if (isValue(monthlyGOI, newMarketValue)) {
                  const monthlyGOIValue = getNumberFromString(monthlyGOI);
                  const purchasePriceValue =
                    getNumberFromString(newMarketValue);

                  const yearlyGOI = monthlyGOIValue * 12;
                  const result = purchasePriceValue / yearlyGOI;
                  return Math.round(result * 100) / 100;
                } else {
                  return null;
                }
              },
            },
            {
              type: FormUIOptions.Number,
              label: 'Equity Multiple',
              layout: 'default',
              name: 'newEquityMultiple',
              tooltipHeading: 'Equity Multiple',
              description:
                'The ratio between the total equity and the total cash invested.',
              tooltipFormula: [
                'Equity Multiple',
                '=',
                '(Total Equity',
                '+',
                'Cumulative Cash Flow)',
                '/',
                'Total Cash Invested',
              ],
              unit: '',
              derivedFrom: [
                'equityInProperty',
                'newMonthlyPreTaxCashFlow',
                'cashOut',
              ],
              calcFunction: (
                equityInProperty,
                newMonthlyPreTaxCashFlow,
                cashOut
              ) => {
                if (!isValue(equityInProperty, cashOut)) {
                  return null;
                }

                return yearlyPercentFromYearlyValue(
                  equityInProperty + newMonthlyPreTaxCashFlow * 12,
                  [cashOut > 0 ? 0 : -cashOut]
                );
              },
            },
            {
              type: FormUIOptions.Number,
              label: 'Break Even Ratio',
              tooltipHeading: 'Break Even Ratio',
              description:
                'The % of units that need to be occupied to break even.',
              tooltipFormula: [
                'Break Even Ratio',
                '=',
                '(Yearly Expenses',
                '+',
                'Yearly Loan Payment)',
                '/',
                'Yearly Gross Rent',
              ],
              name: 'breakEvenRatio',
              unit: '%',
              layout: 'default',
              derivedFrom: [
                'monthlyGOI',
                'newMonthlyPaymentWithRehab',
                'totalMonthlyOperatingExpenses',
              ],
              calcFunction: (
                monthlyGOI,
                newMonthlyPaymentWithRehab,
                totalMonthlyOperatingExpenses
              ) => {
                const yearlyLoanPayment = newMonthlyPaymentWithRehab * 12;
                const totalExpenses = totalMonthlyOperatingExpenses * 12;
                const totalIncome = monthlyGOI * 12;

                return (
                  ((totalExpenses + yearlyLoanPayment) / totalIncome) * 100
                );
              },
            },
            {
              type: FormUIOptions.Number,
              label: 'Debt Coverage Ratio',
              name: 'debtCoverageRatio',
              tooltipHeading: 'Debt Coverage Ratio',
              description:
                'The ratio between the net operating income and the monthly debt service.',
              tooltipFormula: [
                'Debt Coverage Ratio',
                '=',
                'Net Operating Income',
                '/',
                'Debt Service',
              ],
              layout: 'default',
              dynamic: true,
              red: 1,
              green: 1.1,
              derivedFrom: ['netMonthlyIncome', 'newMonthlyPaymentWithRehab'],
              calcFunction: (monthlyNOI, monthlyDebtService) => {
                if (!isValue(monthlyNOI, monthlyDebtService)) {
                  return null;
                }

                return monthlyNOI / monthlyDebtService;
              },
            },
            {
              type: FormUIOptions.Number,
              label: 'Debt Yield',
              name: 'debtYield',
              layout: 'default',
              tooltipHeading: 'Debt Yield',
              description:
                'The ratio between the net operating income and the total loan amount.',
              tooltipFormula: [
                'Debt Yield',
                '=',
                'Yearly Net Operating Income',
                '/',
                'Total Loan Amount',
              ],
              unit: '%',
              derivedFrom: ['netMonthlyIncome', 'newMortgageTotal'],
              calcFunction: (monthlyNOI, totalMortgageAmount) => {
                if (!isValue(monthlyNOI, totalMortgageAmount)) {
                  return null;
                }

                return ((monthlyNOI * 12) / totalMortgageAmount) * 100;
              },
            },
          ],
        },
      },
      {
        sectionType: 'twoColumn',
        leftSchema: {
          sectionLabel: 'Value Summary',
          schema: [
            {
              type: FormUIOptions.Money,
              label: 'Market Purchase Price (Est)',
              name: 'marketPurchasePrice',
              tooltipHeading: 'Market Purchase Price',
              description:
                'The estimated market value of the property at the time of purchase',
              doNotCalculate: true,
            },
            {
              type: FormUIOptions.Number,
              label: 'Rehab Length',
              name: 'rehabLength',
              unit: ' months',
              decimalPlaces: 0,
            },
            {
              type: FormUIOptions.Money,
              label: 'New Value',
              name: 'newMarketValue',
              tooltipHeading: 'New Value',
              description:
                'The estimated market value of the property after the rehab',
              emphasize: true,
            },
            // {
            //   type: FormUIOptions.Money,
            //   label: 'New Value (Potential)',
            //   name: 'newMarketValuePotential',
            //   tooltipHeading: 'New Value (Potential)',
            //   description:
            //     'The estimated market value of the property if all units are rented at market rates',
            //   small: true,
            //   condition: {
            //     field: 'enterTotalRentalIncome',
            //     value: [false, undefined, ''],
            //   },
            // },
            {
              type: FormUIOptions.Money,
              label: 'Value Increase',
              name: 'valueIncreaseActual',
              emphasize: true,
              dynamic: true,
              groupStart: true,
              total: true,
              derivedFrom: ['newMarketValue', 'marketPurchasePrice'],
              calcFunction: (newMarketValue, purchasePrice) => {
                return newMarketValue - purchasePrice;
              },
              displayPercentFrom: [
                'valueIncreaseActual',
                'marketPurchasePrice',
              ],
              displayPercentFunction: (forcedAppreciation, purchasePrice) => {
                return (
                  Math.round((forcedAppreciation / purchasePrice) * 100 * 100) /
                  100
                );
              },
              formula: (forcedAppreciation) => [`=`, `${forcedAppreciation}`],
            },
            // {
            //   type: FormUIOptions.Money,
            //   label: 'Value Increase (Potential)',
            //   name: 'valueIncreasePotential',
            //   small: true,
            //   groupEnd: true,
            //   derivedFrom: ['newMarketValuePotential', 'marketPurchasePrice'],
            //   calcFunction: (newMarketValue, purchasePrice) => {
            //     return newMarketValue - purchasePrice;
            //   },
            //   displayPercentFrom: [
            //     'valueIncreasePotential',
            //     'marketPurchasePrice',
            //   ],
            //   displayPercentFunction: (forcedAppreciation, purchasePrice) => {
            //     return (
            //       Math.round((forcedAppreciation / purchasePrice) * 100 * 100) /
            //       100
            //     );
            //   },
            //   formula: (forcedAppreciation) => [`=`, `${forcedAppreciation}`],
            //   condition: {
            //     field: 'enterTotalRentalIncome',
            //     value: [false, undefined, ''],
            //   },
            // },
          ],
        },
        rightSchema: {
          sectionLabel: 'Equity',
          schema: [
            {
              type: FormUIOptions.Money,
              label: 'New Value',
              emphasize: true,
              name: 'newMarketValue',
            },
            // {
            //   type: FormUIOptions.Money,
            //   label: 'New Value (Potential)',
            //   name: 'newMarketValuePotential',
            //   small: true,
            //   condition: {
            //     field: 'enterTotalRentalIncome',
            //     value: [false, undefined, ''],
            //   },
            // },
            {
              type: FormUIOptions.Money,
              label: 'New Mortgage',
              tooltipHaeading: 'Mortgage Amount',
              description: 'Total mortgage amount for the property',
              name: 'newMortgageTotal',
              formula: (totalFinancingAmount) => ['-', totalFinancingAmount],
            },
            {
              type: FormUIOptions.Money,
              label: 'Rehab Financing',
              tooltipHeading: 'Rehab Financing',
              description:
                'Total financing amount for the rehab, not including financing paid off on refinance',
              name: 'rehabFinancingTotalBRRRR',
              derivedFrom: [
                'rehabCostFinancing',
                'payOffOnRefinanceRehab',
                'mortgageAmountRehab',
                'isRehabFinanced',
              ],
              calcFunction: (
                rehabCostFinancing,
                payOffOnRefinanceRehab,
                mortgageAmountRehab,
                isRehabFinanced
              ) => {
                if (!isRehabFinanced) {
                  return 0;
                }

                const rehabAdditionalLoan = Array.isArray(rehabCostFinancing)
                  ? rehabCostFinancing.reduce((acc, curr) => {
                      return !curr.payOffOnRefinanceTable
                        ? acc + convertUnitMoney(curr.mortgageAmountTable)
                        : acc;
                    }, 0)
                  : 0;

                const rehabOutstandingLoan = payOffOnRefinanceRehab
                  ? 0
                  : mortgageAmountRehab;
                return rehabOutstandingLoan + rehabAdditionalLoan;
              },
              formula: (rehabFinancingAmount) => [
                `-`,
                `${rehabFinancingAmount}`,
              ],
            },
            {
              type: FormUIOptions.Money,
              label: 'Equity',
              name: 'equityInProperty',
              emphasize: true,
              dynamic: true,
              total: true,
              derivedFrom: [
                'newMarketValue',
                'newMortgageTotal',
                'rehabFinancingTotalBRRRR',
              ],
              calcFunction: (
                newMarketValue,
                totalMortgageAmount,
                rehabFinancingTotalBRRRR
              ) => {
                return (
                  newMarketValue -
                  getSum(totalMortgageAmount, rehabFinancingTotalBRRRR)
                );
              },
              formula: (newEquity) => [`=`, `${newEquity}`],
            },
            // {
            //   type: FormUIOptions.Money,
            //   label: 'Equity (Potential)',
            //   name: 'equityInPropertyPotential',
            //   small: true,
            //   groupEnd: true,
            //   derivedFrom: [
            //     'newMarketValuePotential',
            //     'newMortgageTotal',
            //     'rehabFinancingTotalBRRRR',
            //   ],
            //   calcFunction: (
            //     newMarketValue,
            //     totalMortgageAmount,
            //     rehabFinancingTotalBRRRR
            //   ) => {
            //     return (
            //       newMarketValue -
            //       getSum(totalMortgageAmount, rehabFinancingTotalBRRRR)
            //     );
            //   },
            //   formula: (newEquity) => [`=`, `${newEquity}`],
            //   condition: {
            //     field: 'enterTotalRentalIncome',
            //     value: [false, undefined, ''],
            //   },
            // },
          ],
        },
      },
      {
        sectionType: 'twoColumn',
        leftSchema: {
          sectionLabel: 'Financing Details',
          schema: [
            {
              type: FormUIOptions.Money,
              label: 'New Value',
              name: 'newMarketValue',
              unit: '$',
            },
            {
              type: FormUIOptions.Money,
              label: 'New Mortgage',
              name: 'newMortgageTotal',
              dropDown: true,
              formula: (newMortgageTotal) => [`-`, `${newMortgageTotal}`],
            },

            {
              type: FormUIOptions.Money,
              label: 'Mortgage Amount',
              layout: 'default',
              name: 'newMortgageAmount',
              dropDownDependency: 'newMortgageTotal',
              unit: '$',
              derivedFrom: ['loanToValueRatio', 'newMarketValue'],
              calcFunction: (loanToValueRatio, newMarketValue) => {
                return (
                  (loanToValueRatio * getNumberFromString(newMarketValue)) / 100
                );
              },
            },
            {
              type: FormUIOptions.Money,
              label: 'Insurance Premium',
              layout: 'default',
              name: 'cmhcPremiumNew',
              dropDownDependency: 'newMortgageTotal',
              condition: [
                {
                  field: 'shouldCalculateCMHCNew',
                  value: true,
                },
                {
                  field: 'propertyInCanada',
                  value: [undefined, null, 1, ''],
                },
              ],
            },
            {
              type: FormUIOptions.Number,
              label: 'Interest Rate',
              layout: 'default',
              name: 'newInterestRate',
              unit: '%',
              dropDownDependency: 'newMortgageTotal',
            },
            {
              type: FormUIOptions.YesNo,
              label: 'Interest-Only',
              layout: 'default',
              name: 'newMortgageInterestOnly',
              dropDownDependency: 'newMortgageTotal',
            },
            {
              type: FormUIOptions.YesNo,
              label: 'Interest paid monthly?',
              layout: 'default',
              name: 'newInterestOnlyMonthly',
              dropDownDependency: 'newMortgageTotal',
              condition: {
                field: 'newMortgageInterestOnly',
                value: true,
              },
            },
            {
              type: FormUIOptions.Number,
              label: 'Loan Period',
              layout: 'default',
              name: 'newInterestOnlyLoanPeriod',
              unit: 'years',
              dropDownDependency: 'newMortgageTotal',
              condition: {
                field: 'newMortgageInterestOnly',
                value: true,
              },
            },
            {
              type: FormUIOptions.Number,
              label: 'Amortization',
              layout: 'default',
              name: 'newAmortization',
              unit: 'years',
              dropDownDependency: 'newMortgageTotal',
              condition: {
                field: 'newMortgageInterestOnly',
                value: false,
              },
            },
            {
              type: FormUIOptions.Number,
              label: 'Loan-to-Value',
              layout: 'default',
              name: 'loanToValueRatio',
              unit: '%',
              dropDownDependency: 'newMortgageTotal',
            },
            {
              type: FormUIOptions.Money,
              label: 'Monthly Payment',
              layout: 'default',
              name: 'newFirstMonthlyPayment',
              unit: '$',
              groupEnd: true,
              dropDownDependency: 'newMortgageTotal',
              derivedFrom: [
                'newInterestRate',
                'newMortgageAmount',
                'newAmortization',
                'newMortgageInterestOnly',
                'newInterestOnlyMonthly',
              ],
              calcFunction: (
                interestRate,
                mortgageAmount,
                amortization,
                mortgageInterestOnly,
                interestOnlyMonthly
              ) => {
                return mortgageFormSectionCalculator(
                  mortgageAmount,
                  interestRate,
                  mortgageInterestOnly,
                  interestOnlyMonthly,
                  amortization
                );
              },
            },
            {
              type: FormUIOptions.Table,
              label: 'Additional Financing',
              name: 'BRRRRAdditionalFinancing',
              dropDownDependency: 'newMortgageTotal',
              schema: [
                {
                  type: FormUIOptions.Text,
                  name: 'loanNameTable',
                  label: 'Loan Name',
                  dropDownDependency: 'newMortgageTotal',
                },
                {
                  type: FormUIOptions.Money,
                  label: 'Loan Amount',
                  layout: 'default',
                  name: 'mortgageAmountTable',
                  unit: '$',
                  dropDownDependency: 'newMortgageTotal',
                },
                {
                  type: FormUIOptions.YesNo,
                  label: 'Interest-Only',
                  layout: 'default',
                  name: 'mortgageInterestOnlyTable',
                  dropDownDependency: 'newMortgageTotal',
                  condition: {
                    field: 'mortgageInterestOnlyTable',
                    value: true,
                  },
                },
                {
                  type: FormUIOptions.Number,
                  label: 'Interest Rate',
                  layout: 'default',
                  name: 'interestRateTable',
                  dropDownDependency: 'newMortgageTotal',
                  unit: '%',
                },
                {
                  type: FormUIOptions.YesNo,
                  label: 'Interest paid monthly?',
                  layout: 'default',
                  name: 'mortgageInterestOnlyTable',
                  dropDownDependency: 'newMortgageTotal',
                  condition: {
                    field: 'mortgageInterestOnlyTable',
                    value: true,
                  },
                },
                {
                  type: FormUIOptions.Number,
                  label: 'Loan Period',
                  layout: 'default',
                  unit: 'years',
                  name: 'interestOnlyLoanPeriodTable',
                  dropDownDependency: 'newMortgageTotal',
                  condition: {
                    field: 'mortgageInterestOnlyTable',
                    value: true,
                  },
                },
                {
                  type: FormUIOptions.Number,
                  label: 'Amortization',
                  layout: 'default',
                  name: 'amortizationTable',
                  unit: 'years',
                  dropDownDependency: 'newMortgageTotal',
                  condition: {
                    field: 'mortgageInterestOnlyTable',
                    value: false,
                  },
                },
                {
                  type: FormUIOptions.Number,
                  label: 'Loan To Value',
                  layout: 'default',
                  name: 'loanToValueTable',
                  unit: 'years',
                  dropDownDependency: 'newMortgageTotal',
                  mainValue: 'percent',
                  displayPercentFrom: ['mortgageAmountTable', 'newMarketValue'],
                  displayPercentFunction: (
                    mortgageAmountTable,
                    purchasePrice
                  ) => {
                    return (
                      yearlyPercentFromYearlyValue(mortgageAmountTable, [
                        purchasePrice,
                      ]) || ' -'
                    );
                  },
                },
                {
                  type: FormUIOptions.Money,
                  label: 'Monthly Payment',
                  layout: 'default',
                  name: 'monthlyPaymentTable',
                  groupEnd: true,
                  unit: '$',
                  dropDownDependency: 'newMortgageTotal',
                },
              ],
            },
            {
              type: FormUIOptions.Money,
              label: 'Rehab Financing',
              name: 'rehabFinancingTotalBRRRR',
              dropDown: true,
              condition: {
                field: 'isRehabFinanced',
                value: true,
              },
              formula: (rehabFinancingTotalBRRRR) => [
                `-`,
                `${rehabFinancingTotalBRRRR}`,
              ],
            },
            {
              type: FormUIOptions.Money,
              label: 'Loan Amount',
              layout: 'default',
              name: 'mortgageAmountRehab',
              unit: '$',
              dropDownDependency: 'rehabFinancingTotalBRRRR',
            },
            {
              type: FormUIOptions.YesNo,
              label: 'Pay off on refinance?',
              name: 'payOffOnRefinanceRehab',
              dropDownDependency: 'rehabFinancingTotalBRRRR',
            },
            {
              type: FormUIOptions.YesNo,
              label: 'Interest-Only',
              layout: 'default',
              name: 'mortgageInterestOnlyRehab',
              dropDownDependency: 'rehabFinancingTotalBRRRR',
              condition: {
                field: 'mortgageInterestOnlyTable',
                value: true,
              },
            },
            {
              type: FormUIOptions.Number,
              label: 'Interest Rate',
              layout: 'default',
              name: 'interestRateRehab',
              dropDownDependency: 'rehabFinancingTotalBRRRR',
              unit: '%',
            },
            {
              type: FormUIOptions.YesNo,
              label: 'Interest paid monthly?',
              layout: 'default',
              name: 'mortgageInterestOnlyRehab',
              dropDownDependency: 'rehabFinancingTotalBRRRR',
              condition: {
                field: 'mortgageInterestOnlyRehab',
                value: true,
              },
            },
            {
              type: FormUIOptions.Number,
              label: 'Loan Period',
              layout: 'default',
              unit: 'years',
              name: 'interestOnlyLoanPeriodRehab',
              dropDownDependency: 'rehabFinancingTotalBRRRR',
              condition: {
                field: 'mortgageInterestOnlyRehab',
                value: true,
              },
            },
            {
              type: FormUIOptions.Number,
              label: 'Amortization',
              layout: 'default',
              name: 'amortizationRehab',
              unit: 'years',
              dropDownDependency: 'rehabFinancingTotalBRRRR',
              condition: {
                field: 'mortgageInterestOnlyRehab',
                value: false,
              },
            },
            {
              type: FormUIOptions.Number,
              label: 'Loan To Value',
              layout: 'default',
              name: 'loanToValueRehab',
              unit: 'years',
              dropDownDependency: 'rehabFinancingTotalBRRRR',
              mainValue: 'percent',
              displayPercentFrom: ['mortgageAmountRehab', 'purchasePrice'],
              displayPercentFunction: (mortgageAmountTable, purchasePrice) => {
                return (
                  yearlyPercentFromYearlyValue(mortgageAmountTable, [
                    purchasePrice,
                  ]) || ' -'
                );
              },
            },
            {
              type: FormUIOptions.Money,
              label: 'Monthly Payment',
              layout: 'default',
              name: 'monthlyPaymentRehab',
              derivedFrom: [
                'mortgageAmountRehab',
                'interestRateRehab',
                'mortgageInterestOnlyRehab',
                'interestOnlyMonthlyRehab',
                'amortizationRehab',
              ],
              calcFunction: (
                mortgageAmountRehab,
                interestRateRehab,
                mortgageInterestOnlyRehab,
                interestOnlyMonthlyRehab,
                amortizationRehab
              ) => {
                return mortgageFormSectionCalculator(
                  mortgageAmountRehab,
                  interestRateRehab,
                  mortgageInterestOnlyRehab,
                  interestOnlyMonthlyRehab,
                  amortizationRehab
                );
              },
              groupEnd: true,
              unit: '$',
              dropDownDependency: 'rehabFinancingTotalBRRRR',
            },
            {
              type: FormUIOptions.Table,
              label: 'Additional Financing',
              name: 'rehabCostFinancing',
              dropDownDependency: 'rehabFinancingTotalBRRRR',
              schema: [
                {
                  type: FormUIOptions.Text,
                  name: 'loanNameTable',
                  label: 'Loan Name',
                  dropDownDependency: 'rehabFinancingTotalBRRRR',
                },
                {
                  type: FormUIOptions.YesNo,
                  label: 'Payoff on Refinance',
                  layout: 'default',
                  name: 'payOffOnRefinanceTable',
                  dropDownDependency: 'rehabFinancingTotalBRRRR',
                  derivedFrom: ['payOffOnRefinanceTable'],
                  calcFunction: (payOffOnRefinanceTable) => {
                    return payOffOnRefinanceTable ? true : false;
                  },
                },
                {
                  type: FormUIOptions.Money,
                  label: 'Loan Amount',
                  layout: 'default',
                  name: 'mortgageAmountTable',
                  unit: '$',
                  dropDownDependency: 'rehabFinancingTotalBRRRR',
                },
                {
                  type: FormUIOptions.YesNo,
                  label: 'Interest-Only',
                  layout: 'default',
                  name: 'mortgageInterestOnlyTable',
                  dropDownDependency: 'rehabFinancingTotalBRRRR',
                  condition: {
                    field: 'mortgageInterestOnlyTable',
                    value: true,
                  },
                },
                {
                  type: FormUIOptions.Number,
                  label: 'Interest Rate',
                  layout: 'default',
                  name: 'interestRateTable',
                  dropDownDependency: 'rehabFinancingTotalBRRRR',
                  unit: '%',
                },
                {
                  type: FormUIOptions.YesNo,
                  label: 'Interest paid monthly?',
                  layout: 'default',
                  name: 'mortgageInterestOnlyTable',
                  dropDownDependency: 'rehabFinancingTotalBRRRR',
                  condition: {
                    field: 'mortgageInterestOnlyTable',
                    value: true,
                  },
                },
                {
                  type: FormUIOptions.Number,
                  label: 'Loan Period',
                  layout: 'default',
                  unit: 'years',
                  name: 'interestOnlyLoanPeriodTable',
                  dropDownDependency: 'rehabFinancingTotalBRRRR',
                  condition: {
                    field: 'mortgageInterestOnlyTable',
                    value: true,
                  },
                },
                {
                  type: FormUIOptions.Number,
                  label: 'Amortization',
                  layout: 'default',
                  name: 'amortizationTable',
                  unit: 'years',
                  dropDownDependency: 'rehabFinancingTotalBRRRR',
                  condition: {
                    field: 'mortgageInterestOnlyTable',
                    value: false,
                  },
                },
                {
                  type: FormUIOptions.Number,
                  label: 'Loan To Value',
                  layout: 'default',
                  name: 'loanToValueTable',
                  unit: 'years',
                  dropDownDependency: 'rehabFinancingTotalBRRRR',
                  mainValue: 'percent',
                  displayPercentFrom: ['mortgageAmountTable', 'newMarketValue'],
                  displayPercentFunction: (
                    mortgageAmountTable,
                    purchasePrice
                  ) => {
                    return (
                      yearlyPercentFromYearlyValue(mortgageAmountTable, [
                        purchasePrice,
                      ]) || ' -'
                    );
                  },
                },
                {
                  type: FormUIOptions.Money,
                  label: 'Monthly Payment',
                  layout: 'default',
                  name: 'monthlyPaymentTable',
                  groupEnd: true,
                  unit: '$',
                  dropDownDependency: 'rehabFinancingTotalBRRRR',
                },
              ],
            },
            {
              type: FormUIOptions.Money,
              label: 'New Equity',
              name: 'equityInProperty',
              total: true,
              emphasize: true,
              derivedFrom: [
                'newMortgageTotal',
                'rehabFinancingTotalBRRRR',
                'isRehabFinanced',
                'newMarketValue',
              ],
              calcFunction: (
                newMortgageTotal,
                rehabFinancingTotalBRRRR,
                isRehabFinanced,
                newMarketValue
              ) => {
                if (!isRehabFinanced) {
                  return newMarketValue - newMortgageTotal;
                } else {
                  return (
                    newMarketValue -
                    newMortgageTotal -
                    (rehabFinancingTotalBRRRR || 0)
                  );
                }
              },
              formula: (equityInProperty) => [`=`, `${equityInProperty}`],
            },
            {
              type: FormUIOptions.Money,
              label: 'New Mortgage',
              name: 'newFinancingTotal',
              groupStart: true,
              derivedFrom: ['newMortgageTotal'],
              calcFunction: (newMortgageTotal) => {
                return newMortgageTotal;
              },
            },
            {
              type: FormUIOptions.Money,
              label: 'Original Mortgage',
              name: 'totalMortgageAmountNoCMHC',
              tooltipHeading: 'Original Mortgage',
              description:
                'The original mortgage amount not including insurance premium',
              unit: '$',
              derivedFrom: [
                'totalMortgageAmount',
                'cmhcPremium',
                'shouldCalculateCMHC',
                'mortgageInterestOnly',
                'propertyInCanada',
              ],
              calcFunction: (
                totalMortgageAmount,
                cmhcPremium,
                shouldCalculateCMHC,
                mortgageInterestOnly,
                propertyInCanada
              ) => {
                if (
                  !shouldCalculateCMHC ||
                  mortgageInterestOnly ||
                  propertyInCanada === 0
                ) {
                  return totalMortgageAmount;
                } else {
                  return totalMortgageAmount - (cmhcPremium || 0);
                }
              },
              formula: (purchasePrice) => [`-`, `${purchasePrice}`],
            },

            {
              type: FormUIOptions.Money,
              label: 'Insurance Premium',
              name: 'cmhcPremium',
              doNotCalculate: true,
              dropDownDependency: 'purchasePrice',
              condition: [
                {
                  field: 'shouldCalculateCMHC',
                  value: true,
                },
                { field: 'mortgageInterestOnly', value: false },
                { field: 'propertyInCanada', value: [undefined, null, 1, ''] },
              ],
              formula: (cmhcPremium) => [`-`, `${cmhcPremium}`],
            },
            {
              type: FormUIOptions.Money,
              label: 'Insurance Premium',
              name: 'cmhcPremiumNew',
              unit: '$',
              formula: (cmhcPremiumNew) => [`-`, `${cmhcPremiumNew}`],
              condition: [
                {
                  field: 'shouldCalculateCMHCNew',
                  value: true,
                },
                {
                  field: 'propertyInCanada',
                  value: [undefined, null, 1, ''],
                },
              ],
            },
            {
              type: FormUIOptions.Money,
              label: 'Cash Invested',
              name: 'totalCashInvestedWithDownPayment',
              dropDown: true,
              unit: '$',
              derivedFrom: [
                'totalRehabCosts',
                'rehabFinancingTotalBRRRR',
                'totalCashRequiredToClose',
                'totalCarryingCosts',
                'downPaymentAmount',
                'totalRefinancingCosts',
                'enterTotalRentalIncome',
              ],
              calcFunction: (
                totalRehabCosts,
                rehabFinancingTotalBRRRR,
                totalCashRequiredToClose,
                totalCarryingCosts,
                downPaymentAmount,
                totalRefinancingCosts,
                enterTotalRentalIncome
              ) => {
                const carryingCosts = enterTotalRentalIncome
                  ? 0
                  : totalCarryingCosts;

                return getSum(
                  totalRehabCosts - (rehabFinancingTotalBRRRR || 0),
                  totalCashRequiredToClose,
                  carryingCosts,
                  downPaymentAmount || 0,
                  totalRefinancingCosts || 0
                );
              },
              formula: (totalCashInvested) => [`-`, `${totalCashInvested}`],
            },
            {
              type: FormUIOptions.Money,
              label: 'Down Payment',
              name: 'downPaymentAmount',
              doNotCalculate: true,
              dropDownDependency: 'totalCashInvestedWithDownPayment',
            },
            {
              type: FormUIOptions.Money,
              label: 'Rehab Costs Cash',
              name: 'rehabCostUnfinancedBRRRR',
              dropDownDependency: 'totalCashInvestedWithDownPayment',
              derivedFrom: ['totalRehabCosts', 'rehabFinancingTotalBRRRR'],
              calcFunction: (totalRehabCosts, rehabFinancingTotalBRRRR) => {
                return totalRehabCosts - (rehabFinancingTotalBRRRR || 0);
              },
              unit: '$',
            },
            {
              type: FormUIOptions.Money,
              label: 'Closing Cost(s) (Purchase)',
              doNotCalculate: true,
              dropDownDependency: 'totalCashInvestedWithDownPayment',
              layout: 'default',
              name: 'totalCashRequiredToClose',
              unit: '$',
            },
            {
              type: FormUIOptions.Money,
              label: 'Refinancing Costs',
              dropDownDependency: 'totalCashInvestedWithDownPayment',
              layout: 'default',
              name: 'totalRefinancingCosts',
            },
            {
              type: FormUIOptions.Money,
              label: 'Carrying Costs',
              dropDownDependency: 'totalCashInvestedWithDownPayment',
              layout: 'default',
              description:
                'Total holding/carrying costs (including mortgage payments) for the rehab period',
              name: 'totalCarryingCosts',
              unit: '$',
              condition: {
                field: 'enterTotalRentalIncome',
                value: [false, null, undefined, ''],
              },
            },
            {
              type: FormUIOptions.Money,
              label: ['Cash / Profit', 'Cash Left In'],
              name: 'cashOut',
              emphasize: true,
              total: true,
              dynamic: true,
              absoluteValue: true,
              unit: '$',
              formula: (cashOut) => [`=`, `${cashOut}`],
              derivedFrom: [
                'newFinancingTotal',
                'totalCashInvestedWithDownPayment',
                'totalMortgageAmountNoCMHC',
                'cmhcPremiumNew',
                'shouldCalculateCMHCNew',
                'propertyInCanada',
              ],
              calcFunction: (
                newFinancingTotal,
                totalCashInvestedWithDownPayment,
                totalMortgageAmountNoCMHC,
                cmhcPremiumNew,
                shouldCalculateCMHCNew,
                propertyInCanada
              ) => {
                if (
                  isValue(
                    newFinancingTotal,
                    totalCashInvestedWithDownPayment,
                    totalMortgageAmountNoCMHC
                  )
                ) {
                  let cashOut;
                  cashOut =
                    newFinancingTotal -
                    getSum(
                      totalCashInvestedWithDownPayment,
                      totalMortgageAmountNoCMHC
                    );

                  if (
                    shouldCalculateCMHCNew &&
                    cmhcPremiumNew &&
                    cashOut &&
                    propertyInCanada !== 0
                  ) {
                    return cashOut - cmhcPremiumNew;
                  }

                  return cashOut;
                }
                return null;
              },
            },
          ],
        },
        rightSchema: {
          sectionLabel: 'Cash Flow',
          sectionSubLabel: 'Monthly',
          reportButton: 'cashflow_report',
          annualValue: true,
          schema: [
            {
              type: FormUIOptions.Money,
              label: 'Rental Income',
              name: 'totalRentalIncomeBuyAndHold',
              tooltipHeading: 'Gross Rent',
              description: 'Rental income across all units',
              unit: '$',
              derivedFrom: ['rentalIncomePreVacancy'],
              calcFunction: (rentalIncomePreVacancy) => {
                return rentalIncomePreVacancy;
              },
              dropDown: true,
              tooltipHeading: 'Additional Income',
              condition: [
                {
                  field: 'enterTotalRentalIncome',
                  value: false,
                },
              ],
            },
            {
              type: FormUIOptions.Money,
              label: 'Rental Income',
              name: 'totalRentalIncomeBuyAndHold',
              tooltipHeading: 'Gross Rent',
              description: 'Rental income across all units',
              unit: '$',
              derivedFrom: ['rentalIncomePreVacancy'],
              calcFunction: (rentalIncomePreVacancy) => {
                return rentalIncomePreVacancy;
              },
              tooltipHeading: 'Additional Income',
              condition: [
                {
                  field: 'enterTotalRentalIncome',
                  value: true,
                },
              ],
            },
            {
              type: FormUIOptions.Table,
              label: '',
              name: 'grossIncomePerUnit',
              dropDownDependency: 'totalRentalIncomeBuyAndHold',
              condition: [
                {
                  field: 'enterTotalRentalIncome',
                  value: false,
                },
              ],
              schema: [
                {
                  type: FormUIOptions.Text,
                  name: 'unitName',
                  label: 'Unit Name',
                  dropDownDependency: 'totalRentalIncomeBuyAndHold',
                  derivedFrom: ['unitName'],
                  calFunction: (name) => {
                    return `Unit ${name}`;
                  },
                },
                {
                  type: FormUIOptions.UnitMoney,
                  label: 'Amount per month',
                  showMonthly: true,
                  unit: '$',
                  isConstant: true,
                  displayMonthlyFrom: ['rent'],
                  displayMonthlyFunction: (rent) => {
                    return rent;
                  },
                  dropDownDependency: 'totalRentalIncomeBuyAndHold',
                },
              ],
            },
            // {
            //   type: FormUIOptions.Money,
            //   label: 'Rental Income',
            //   layout: 'default',
            //   name: 'totalRentalIncome',
            //   tooltipHeading: 'Rental Income',
            //   description: 'Rental income across all units',
            //   unit: '$',
            //   derivedFrom: ['rentalIncomePreVacancy'],
            //   calcFunction: (rentalIncomePreVacancy) => {
            //     return rentalIncomePreVacancy;
            //   },
            // },
            {
              type: FormUIOptions.Money,
              label: 'Vacancy',
              layout: 'default',
              name: 'vacancy',
              unit: '$',
              derivedFrom: ['vacancyDeduction'],
              calcFunction: (vacancyDeduction) => {
                const vacancy = convertUnitMoney(vacancyDeduction);
                if (isValue(vacancy)) {
                  return Math.round(vacancy * 100) / 100;
                } else {
                  return 0;
                }
              },
              formula: (vacancy) => ['-', `${vacancy}`],
              displayPercentFrom: ['vacancyDeduction', 'totalRentalIncome'],
              displayPercentFunction: (vacancyDeduction, totalRentalIncome) => {
                if (isValue(vacancyDeduction) && isValue(totalRentalIncome)) {
                  return Math.round(
                    (vacancyDeduction / totalRentalIncome) * 100
                  );
                } else {
                  return 0;
                }
              },
            },
            {
              type: FormUIOptions.Money,
              label: 'Additional Income',
              layout: 'default',
              name: 'totalOtherIncome',
              dropDown: true,
              unit: '$',
              formula: (otherIncome) => ['+', `${otherIncome}`],
            },
            {
              type: FormUIOptions.Table,
              label: '',
              name: 'otherPropertyIncome',
              dropDownDependency: 'totalOtherIncome',
              condition: [
                {
                  field: 'cashPurchase',
                  value: false,
                },
                {
                  field: 'enterTotalOtherIncome',
                  value: [false, undefined, ''],
                },
              ],
              schema: [
                {
                  type: FormUIOptions.Text,
                  name: 'otherIncomeName',
                  label: 'Other Income',
                  dropDownDependency: 'totalOtherIncome',
                },
                {
                  type: FormUIOptions.UnitMoney,
                  label: 'Amount',
                  layout: 'default',
                  name: 'otherIncomeAmount',
                  unit: '$',
                  dropDownDependency: 'totalOtherIncome',
                },
              ],
            },
            {
              type: FormUIOptions.Money,
              label: 'Gross Income (GOI)',
              layout: 'default',
              name: 'monthlyGOI',
              total: true,
              emphasize: true,
              tooltipHeading: 'Gross Operating Income',
              description:
                'Rental and other income across all units adjusted for vacancy rate',
              unit: '$',
              derivedFrom: [
                'totalGrossRentalIncome',
                'totalAdditionalIncomeBuyAndHold',
              ],
              calcFunction: (
                totalGrossRentalIncome,
                totalAdditionalIncomeBuyAndHold
              ) => {
                return getSum(
                  totalGrossRentalIncome,
                  totalAdditionalIncomeBuyAndHold
                );
              },
              formula: (monthlyGOI) => ['=', `${monthlyGOI}`],
            },
            {
              type: FormUIOptions.Money,
              label: 'Total Operating Expenses',
              layout: 'default',
              name: 'totalMonthlyOperatingExpenses',
              dropDown: true,
              unit: '$',
              derivedFrom: ['totalOperatingExpenses'],
              calcFunction: (totalOperatingExpenses) => totalOperatingExpenses,
              formula: (totalMonthlyOperatingExpenses) => [
                '-',
                `${totalMonthlyOperatingExpenses}`,
              ],
            },
            {
              type: FormUIOptions.Table,
              label: '',
              name: 'operatingExpenses',
              dropDownDependency: 'totalMonthlyOperatingExpenses',
              condition: [
                {
                  field: 'cashPurchase',
                  value: false,
                },
                {
                  field: 'enterTotalExpenses',
                  value: [false, undefined, ''],
                },
              ],
              schema: [
                {
                  type: FormUIOptions.Text,
                  name: 'expenseName',
                  label: 'Expense',
                  dropDownDependency: 'totalMonthlyOperatingExpenses',
                },
                {
                  type: FormUIOptions.UnitMoney,
                  derivedFrom: 'rentalIncomePreVacancy',
                  label: 'Amount',
                  layout: 'default',
                  name: 'expenseAmount',
                  unit: '$',
                  dropDownDependency: 'totalMonthlyOperatingExpenses',
                },
              ],
            },
            {
              type: FormUIOptions.Money,
              label: 'Net Income (NOI)',
              layout: 'default',
              name: 'netMonthlyIncome',
              total: true,
              emphasize: true,
              unit: '$',
              derivedFrom: ['monthlyGOI', 'totalMonthlyOperatingExpenses'],
              calcFunction: (monthlyGOI, totalMonthlyOperatingExpenses) => {
                if (
                  isValue(monthlyGOI) &&
                  isValue(totalMonthlyOperatingExpenses)
                ) {
                  return (
                    Math.round(
                      monthlyGOI * 100 - totalMonthlyOperatingExpenses * 100
                    ) / 100
                  );
                } else {
                  return null;
                }
              },
              formula: (netMonthlyIncome) => ['=', `${netMonthlyIncome}`],
            },
            {
              type: FormUIOptions.Money,
              label: 'Loan Payments',
              layout: 'default',
              name: 'newMonthlyPaymentWithRehab',
              unit: '$',
              derivedFrom: [
                'newMonthlyPayment',
                'rehabCostFinancing',
                'monthlyPaymentRehab',
                'payOffOnRefinanceRehab',
                'isRehabFinanced',
              ],
              calcFunction: (
                newMonthlyPayment,
                rehabCostFinancing,
                monthlyPaymentRehab,
                payOffOnRefinanceRehab,
                isRehabFinanced
              ) => {
                if (!isRehabFinanced) {
                  return newMonthlyPayment;
                }

                const rehabAdditionalPayments = Array.isArray(
                  rehabCostFinancing
                )
                  ? rehabCostFinancing.reduce((acc, curr) => {
                      return !curr.payOffOnRefinanceTable
                        ? acc + convertUnitMoney(curr.monthlyPaymentTable)
                        : acc;
                    }, 0)
                  : 0;

                const rehabPayment = payOffOnRefinanceRehab
                  ? 0
                  : monthlyPaymentRehab;

                return (
                  newMonthlyPayment + rehabPayment + rehabAdditionalPayments
                );
              },
              formula: (newMortgageAmount) => ['-', `${newMortgageAmount}`],
            },
            {
              type: FormUIOptions.Money,
              label: 'Cash Flow',
              total: true,
              emphasize: true,
              dynamic: true,
              layout: 'default',
              name: 'newMonthlyPreTaxCashFlow',
              unit: '$',
              formula: (newMonthlyPreTaxCashFlow) => [
                '=',
                `${newMonthlyPreTaxCashFlow}`,
              ],
            },
          ],
        },
      },
    ],
  },
  keyMetrics: [
    {
      sectionValue: 'keyMetrics1',
      layout: 'column',
      //sublabel and subname are to have a smaller metric displayed below the main metric. subConditionalOn allows you to change whether to display the sublabel differently based on the main value of the metric or the subvalue.
      //If subConditionalOn is not specified, it will default to the main value. An array of two labels can be passed in, array[0] is if the conditional value is positive, array[1] is if the conditional value is negative.
      schema: [
        {
          type: FormUIOptions.Money,
          label: 'New Value',
          layout: 'default',
          name: 'newMarketValue',
          unit: '$',
          subLabel: [<FaArrowUp className="mb-[0.1rem]" />, <FaArrowDown />],
          subName: 'forcedAppreciation',
          subConditionalOn: 'subValue',
        },

        {
          type: FormUIOptions.Money,
          label: 'New Equity',
          description: 'The new equity in the property after refinancing',
          tooltipHeading: 'New Equity',
          tooltipFormula: ['New Equity', '=', 'New Value', '-', 'New Loan'],
          layout: 'default',
          name: 'equityInProperty',
        },
        {
          type: FormUIOptions.Money,
          label: 'Total Cash Invested',
          layout: 'default',
          name: 'totalCashInvestedWithDownPayment',
          tooltipHeading: 'Total Cash Invested',
          description:
            'The total of the original downpayment, closing costs, rehab costs and carrying costs.',
          tooltipFormula: [
            'Total Cash Invested',
            '=',
            'Down Payment',
            '+',
            'Closing Costs',
            '+',
            'Rehab Costs Cash',
            '+',
            'Carrying Costs',
            '+',
            'Refinancing Costs',
          ],
          subLabel: [<span>BRRRR</span>, <span>BRRRR</span>],
          subName: 'BRRRRCostOnlyCash',
        },
        //absolute value will display the metric as a positive value, regardless of whether the value is positive or negative. This is the case for cash out.
        {
          type: FormUIOptions.Money,
          label: ['Profit After Refinance', 'Cash Left In'],
          absoluteValue: true,
          dynamic: true,
          layout: 'default',
          name: 'cashOut',
          tooltipHeading: 'Profit After Refinance / Cash Left In',
          description:
            'The amount of cash left in the property or taken out after refinancing',
          tooltipFormula: [
            'Profit After Refinance / Cash Left In',
            '=',
            'New Loan',
            '-',
            'Original Loan',
            '-',
            'Insurance Premium',
            '-',
            'Total Cash Invested',
          ],
          unit: '$',
          subLabel: [
            <span className="flex justify-center items-center mr-[2px] gap-[2px]">
              <TiPlus />
              Investment
            </span>,
            <span className="">-Removed</span>,
          ],
          subName: 'investmentRecovered',
          subConditionalOn: 'mainValue',
        },
        {
          type: FormUIOptions.Money,
          label: 'Cash Flow',
          layout: 'default',
          tooltipHeading: 'Cash Flow',
          description: 'The monthly cash flow after refinancing',
          tooltipFormula: [
            'Cash Flow',
            '=',
            'Monthly Income',
            '-',
            'Monthly Expenses',
          ],
          name: 'newMonthlyPreTaxCashFlow',
          isAnnual: false,
          showMonthlyAnnual: true,
          dynamic: true,
          unit: '$',
        },
      ],
    },
  ],

  investmentMetrics: [
    {
      type: FormUIOptions.Number,
      label: '70% Rule',
      name: 'seventyPercentRuleBRRRRCriteria',
      description:
        'The purchase price should be less than or equal to 70% of the ARV, minus the rehab costs',
      derivedFrom: ['purchasePrice', 'newMarketValue', 'totalRehabCosts'],
      calcFunction: (purchasePrice, newMarketValue, totalRehabCosts) => {
        if (isValue(purchasePrice, newMarketValue, totalRehabCosts)) {
          const result = purchasePrice / (newMarketValue - totalRehabCosts);

          if (result <= 0.7) {
            return {
              value: 1,
              message: `Passes 70% rule (${Math.round(result * 1000) / 10}%).`,
            };
          }
          return {
            value: 0,
            message: `Does not pass 70% rule (${
              Math.round(result * 1000) / 10
            }%).`,
          };
        }
        return {
          value: 2,
          message:
            'Missing value for purchase price, new market value or rehab costs.',
        };
      },

      condition: {
        field: 'screenSeventyPercentRuleBRRRR',
        value: true,
      },
    },
    {
      type: FormUIOptions.Money,
      label: 'Total Profit',
      name: 'totalProfitCriteria',
      description:
        'Compare the total profit to the total profit on the purchase (equity + cash flow - cash needed)',
      derivedFrom: [
        'equityOnPurchase',
        'annualPreTaxCashFlow',
        'totalCashInvestedOnPurchase',
        'equityInProperty',
        'newMonthlyPreTaxCashFlow',
        'cashOut',
        'totalProfitBRRRR',
      ],
      calcFunction: (
        equityOnPurchase,
        annualPreTaxCashFlow,
        totalCashInvestedOnPurchase,
        newEquityBuyAndHold,
        monthlyPreTaxCashFlowBuyAndHold,
        cashOut,
        totalProfitBuyAndHold
      ) => {
        const totalProfitCriteria =
          totalProfitBuyAndHold || criteriaDefaults.totalProfitBRRRR;

        const totalCashInvestedRefi = -cashOut;

        if (
          isValue(
            equityOnPurchase,
            annualPreTaxCashFlow,
            totalCashInvestedOnPurchase,
            newEquityBuyAndHold,
            monthlyPreTaxCashFlowBuyAndHold,
            totalCashInvestedRefi
          )
        ) {
          const totalProfitOnPurchase =
            equityOnPurchase +
            annualPreTaxCashFlow -
            totalCashInvestedOnPurchase;
          const totalProfit =
            newEquityBuyAndHold +
            monthlyPreTaxCashFlowBuyAndHold * 12 -
            totalCashInvestedRefi;

          const difference = totalProfit - totalProfitOnPurchase;

          if (difference >= totalProfitCriteria) {
            return {
              value: 1,
              message: `Total profit of $${getNumberWithCommas(
                Math.round(totalProfit)
              )} is $${getNumberWithCommas(
                Math.round(difference)
              )} higher than the initial of $${getNumberWithCommas(
                Math.round(totalProfitOnPurchase)
              )}.`,
            };
          }
          return {
            value: 0,
            message: `Total profit of $${getNumberWithCommas(
              Math.round(totalProfit)
            )} is $${getNumberWithCommas(
              Math.round(difference)
            )} different than the initial of $${getNumberWithCommas(
              Math.round(totalProfitOnPurchase)
            )}.`,
          };
        }

        return {
          value: 2,
          message: 'Missing value for equity, cash flow, or cash needed.',
        };
      },

      condition: {
        field: 'screenTotalProfitBRRRR',
        value: true,
      },
    },
    {
      type: FormUIOptions.Money,
      label: 'Cash In / Out',
      description: 'Minimum cash out from refinance',
      name: 'cashNeededCriteria',
      derivedFrom: [
        'cashOut',
        'cashOutInitialInvestmentBRRRR',
        'cashOutRehabRefinanceBRRRR',
        'newFinancingTotal',
        'rehabFinancingTotalBRRRR',
        'totalMortgageAmountNoCMHC',
        'cmhcPremiumNew',
        'shouldCalculateCMHCNew',
        'totalRehabCosts',
        'totalCarryingCosts',
        'totalRefinancingCosts',
        'totalCashRequiredToClose',
        'downPaymentAmount',
        'enterTotalRentalIncome',
      ],
      calcFunction: (
        cashOut,
        cashOutInitialInvestmentBRRRR,
        cashOutRehabRefinanceBRRRR,
        newFinancingTotal,
        rehabFinancingTotalBRRRR,
        totalMortgageAmountNoCMHC,
        cmhcPremiumNew,
        shouldCalculateCMHCNew,
        totalRehabCosts,
        totalCarryingCosts,
        totalRefinancingCosts,
        totalCashRequiredToClose,
        downPaymentAmount,
        enterTotalRentalIncome
      ) => {
        const carryingCosts = enterTotalRentalIncome ? 0 : totalCarryingCosts;

        const cashOutInitialInvestment = isValue(cashOutInitialInvestmentBRRRR)
          ? cashOutInitialInvestmentBRRRR
          : criteriaDefaults.cashOutInitialInvestmentBRRRR;
        const cashOutRehabRefinance = isValue(cashOutRehabRefinanceBRRRR)
          ? cashOutRehabRefinanceBRRRR
          : criteriaDefaults.cashOutRehabRefinanceBRRRR;
        const initialInvestment =
          (downPaymentAmount || 0) + (totalCashRequiredToClose || 0);
        const rehabRefinanceCosts =
          (totalRehabCosts || 0) +
          (carryingCosts || 0) +
          (totalRefinancingCosts || 0);
        const cashOutBeforeCosts =
          newFinancingTotal +
          (rehabFinancingTotalBRRRR || 0) -
          totalMortgageAmountNoCMHC -
          (shouldCalculateCMHCNew ? cmhcPremiumNew || 0 : 0);

        if (cashOutInitialInvestment && cashOutRehabRefinance) {
          if (isValue(cashOut)) {
            if (cashOut < 0) {
              return {
                value: 0,
                message: `Cash out of $${getNumberWithCommas(
                  Math.round(cashOutBeforeCosts)
                )} is lower than the minimum of $${getNumberWithCommas(
                  Math.round(rehabRefinanceCosts + initialInvestment)
                )}.`,
              };
            } else {
              return {
                value: 1,
                message: `Cash out of $${getNumberWithCommas(
                  Math.round(cashOutBeforeCosts)
                )} is higher than the minimum of $${getNumberWithCommas(
                  Math.round(rehabRefinanceCosts + initialInvestment)
                )}.`,
              };
            }
          }
        } else if (cashOutInitialInvestment) {
          if (isValue(cashOutBeforeCosts, initialInvestment)) {
            if (cashOutBeforeCosts > initialInvestment) {
              return {
                value: 1,
                message: `Cash out of $${getNumberWithCommas(
                  Math.round(cashOutBeforeCosts)
                )} is higher than the minimum of $${getNumberWithCommas(
                  Math.round(initialInvestment)
                )}.`,
              };
            } else {
              return {
                value: 0,
                message: `Cash out of $${getNumberWithCommas(
                  Math.round(cashOutBeforeCosts)
                )} is lower than the minimum of $${getNumberWithCommas(
                  Math.round(initialInvestment)
                )}.`,
              };
            }
          }
        } else if (cashOutRehabRefinance) {
          if (isValue(rehabRefinanceCosts, cashOutBeforeCosts)) {
            if (cashOutBeforeCosts > rehabRefinanceCosts) {
              return {
                value: 1,
                message: `Cash out of $${getNumberWithCommas(
                  Math.round(cashOutBeforeCosts)
                )} is higher than the minimum of $${getNumberWithCommas(
                  Math.round(rehabRefinanceCosts)
                )}.`,
              };
            } else {
              return {
                value: 0,
                message: `Cash out of $${getNumberWithCommas(
                  Math.round(cashOutBeforeCosts)
                )} is lower than the minimum of $${getNumberWithCommas(
                  Math.round(rehabRefinanceCosts)
                )}.`,
              };
            }
          }
        }

        return {
          value: 2,
          message: 'Missing value for cash out or cash out criteria.',
        };
      },
      condition: {
        field: 'screenCashNeededBRRRR',
        value: true,
      },
    },
    {
      type: FormUIOptions.Number,
      label: '1% Rule',
      name: 'onePercentRuleCriteria',
      description:
        'The monthly gross rent should be at least 1% of the purchase price.',
      derivedFrom: ['monthlyGOI', 'purchasePrice'],
      calcFunction: (monthlyGOI, purchasePrice) => {
        if (isValue(monthlyGOI, purchasePrice)) {
          const monthlyGOIValue = getNumberFromString(monthlyGOI);
          const purchasePriceValue = getNumberFromString(purchasePrice);

          const result = monthlyGOIValue / purchasePriceValue;
          if (result >= 0.01) {
            return {
              value: 1,
              message: `Passes 1% rule (${Math.round(result * 1000) / 10}%).`,
            };
          } else {
            return {
              value: 0,
              message: `Does not pass 1% rule (${
                Math.round(result * 1000) / 10
              }%).`,
            };
          }
        }
        return {
          value: 2,
          message: 'Missing value for monthly GOI or purchase price.',
        };
      },
      condition: {
        field: 'screenOnePercentRuleBRRRR',
        value: true,
      },
    },
    {
      type: FormUIOptions.Number,
      label: '50% Rule',
      name: 'fiftyPercentRuleCriteria',
      description:
        'The monthly expenses should be less than or equal to 50% of the monthly gross rent.',
      derivedFrom: ['totalMonthlyOperatingExpenses', 'monthlyGOI'],
      calcFunction: (totalMonthlyOperatingExpenses, monthlyGOI) => {
        if (isValue(totalMonthlyOperatingExpenses, monthlyGOI)) {
          const totalMonthlyOperatingExpensesValue = getNumberFromString(
            totalMonthlyOperatingExpenses
          );
          const monthlyGOIValue = getNumberFromString(monthlyGOI);

          const result = totalMonthlyOperatingExpensesValue / monthlyGOIValue;
          if (result <= 0.5) {
            return {
              value: 1,
              message: `Passes 50% rule (${Math.round(result * 1000) / 10}%).`,
            };
          } else {
            return {
              value: 0,
              message: `Does not pass 50% rule (${
                Math.round(result * 1000) / 10
              }%).`,
            };
          }
        }
        return {
          value: 2,
          message: 'Missing value for monthly expenses or monthly GOI.',
        };
      },
      condition: {
        field: 'screenFiftyPercentRuleBRRRR',
        value: true,
      },
    },
    {
      type: FormUIOptions.Number,
      label: 'Cash Flow',
      name: 'cashFlowCriteria',
      description:
        'Whether the property passes the minimum cash flow requirement.',
      derivedFrom: ['newMonthlyPreTaxCashFlow', 'minCashFlowBRRRR'],
      calcFunction: (monthlyPreTaxCashFlow, minCashFlowPurchase) => {
        const minCashFlow =
          minCashFlowPurchase || criteriaDefaults.minCashFlowBRRRR;

        if (isValue(monthlyPreTaxCashFlow, minCashFlow)) {
          if (monthlyPreTaxCashFlow >= minCashFlow) {
            return {
              value: 1,
              message: `Passes cash flow rule ($${getNumberWithCommas(
                Math.round(monthlyPreTaxCashFlow)
              )}).`,
            };
          } else {
            return {
              value: 0,
              message: `Does not pass cash flow rule ($${getNumberWithCommas(
                Math.round(monthlyPreTaxCashFlow)
              )}).`,
            };
          }
        }
        return {
          value: 2,
          message: 'Missing value for monthly cash flow.',
        };
      },
      condition: {
        field: 'screenCashFlowBRRRR',
        value: true,
      },
    },
    {
      type: FormUIOptions.Number,
      label: 'Cap Rate (Purchase)',
      name: 'capRateCriteria',
      description: 'Whether the property passes the cap rate requirement.',
      derivedFrom: ['capRate', 'minCapRateBRRRR'],
      calcFunction: (capRateInitial, minCapRatePurchase) => {
        const minCapRate =
          minCapRatePurchase || criteriaDefaults.minCapRateBRRRR;

        if (isValue(capRateInitial, minCapRate)) {
          if (capRateInitial >= minCapRate) {
            return {
              value: 1,
              message: `Passes cap rate rule (${capRateInitial}%)`,
            };
          } else {
            return {
              value: 0,
              message: `Does not pass cap rate rule (${capRateInitial}%)`,
            };
          }
        }
        return {
          value: 2,
          message: 'Missing value for cap rate or minimum cap rate',
        };
      },
      condition: {
        field: 'screenCapRateBRRRR',
        value: true,
      },
    },
    {
      type: FormUIOptions.Number,
      label: 'Total ROI Year 1',
      name: 'totalRoiYear1Criteria',
      description:
        'Whether the property passes the total return on investment requirement.',
      derivedFrom: ['newTotalRoiYear1', 'cashOut', 'minTotalRoiYear1BRRRR'],
      calcFunction: (totalRoiYear1, cashOut, minTotalRoiYear1BRRRR) => {
        const minTotalRoiYear1 =
          minTotalRoiYear1BRRRR || criteriaDefaults.minTotalRoiYear1BRRRR;

        if (isValue(totalRoiYear1, minTotalRoiYear1, cashOut)) {
          const totalCashInvested = -cashOut;

          const divisor = totalCashInvested > 0 ? totalCashInvested : 0;

          const value = (totalRoiYear1 / divisor) * 100;

          if (value >= minTotalRoiYear1) {
            return {
              value: 1,
              message: `Passes total ROI year 1 rule (${Math.round(value)}%)`,
            };
          } else {
            return {
              value: 0,
              message: `Does not pass total ROI year 1 rule (${Math.round(
                value
              )}%)`,
            };
          }
        }
        return {
          value: 2,
          message:
            'Missing value for total ROI year 1 or minimum total ROI year 1',
        };
      },
      condition: {
        field: 'screenTotalRoiYear1BRRRR',
        value: true,
      },
    },
  ],
};
